import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createWorld, scaffoldWorld, reextractCharacters, getAvailableModels, curateWorldStep, updateCompanionProfile, createWsTicket, ModelInfo, CurationStep } from '../api';
import { useAuthStore, useWorldsStore } from '../store';
import { ContentRating, ScaffoldedCharacter, WorldScaffoldingResult, ScaffoldProgressEvent, ScaffoldPhase } from '../types';
import { EditCharacterModal } from './EditCharacterModal';
import { encode } from 'gpt-tokenizer';
import { isMinorApparentAge } from '../utils/companion';

// ============================================
// Content rating description shown next to the picker. Explains what the
// rating means for the story's content, not how the backend routes it.

function ContentRatingDescription({ rating }: { rating: ContentRating }) {
  const hints: Record<ContentRating, { color: string; title: string; body: string }> = {
    standard: {
      color: 'bg-dark-700 border-dark-600 text-dark-200',
      title: 'Standard',
      body:
        'Safe-for-work storytelling. Violence, substance use, and profanity stay tasteful; no explicit sexual content. Great for all-ages adventure, mystery, drama, and genre fiction aimed at a wide audience.',
    },
    mature: {
      color: 'bg-yellow-900/20 border-yellow-700/50 text-yellow-100',
      title: 'Mature',
      body:
        'Adult themes allowed — on-screen violence, substance use, sensuality, and strong language. Explicit sexual content is implied or fades to black rather than depicted. Good for noir, horror, grittier fantasy/sci-fi, and character-driven drama with adult complexity.',
    },
    explicit: {
      color: 'bg-orange-900/20 border-orange-700/50 text-orange-100',
      title: 'Explicit',
      body:
        'Anything goes within platform policy — explicit sex, graphic violence, and taboo themes. Stories follow their own logic wherever the narrative leads. Good for erotic romance, extreme horror, and transgressive literary fiction.',
    },
  };
  const h = hints[rating];
  return (
    <div className={`mt-3 rounded border p-3 text-sm ${h.color}`}>
      <div className="font-medium mb-1">{h.title}</div>
      <div className="opacity-90">{h.body}</div>
    </div>
  );
}

// ============================================
// Constants
// ============================================

const DEFAULT_RULES = [
  'Player agency is paramount - never force outcomes on player actions.',
  'Reveal information gradually - avoid info-dumps.',
  'NPCs have their own goals and will act on them.',
  'Maintain tension and stakes appropriate to the scene.',
  'Show, don\'t tell - use action and dialogue over exposition.',
];

const REASONING_EFFORT_OPTIONS = [
  { value: 'minimal', label: 'Minimal - Quick responses' },
  { value: 'low', label: 'Low - Light reasoning' },
  { value: 'medium', label: 'Medium - Balanced' },
  { value: 'high', label: 'High - Deep thinking' },
  { value: 'max', label: 'Max - Deepest thinking' },
] as const;

// ============================================
// Types
// ============================================

type CreationMode = 'prompt' | 'guided' | 'companion' | 'templates';

// Steps for prompt mode (existing flow)
type PromptStep = 'concept' | 'content' | 'processing' | 'review';

// Steps for guided mode (new flow)
type GuidedStep = 'foundation' | 'setting' | 'characters' | 'conflict' | 'opening' | 'review';

// Combined wizard step type
type WizardStep = 'mode' | 'templates' | 'companion' | 'companion_processing' | PromptStep | GuidedStep;

// Requests belong to the current page, so a saved draft must resume at a
// usable form or completed result, never at a request that no longer exists.
function getResumableStep(step: WizardStep, mode: CreationMode | null, hasResult: boolean): WizardStep {
  if (step === 'processing') return hasResult ? 'review' : 'content';
  if (step === 'companion_processing') return 'companion';
  if (step === 'review' && mode === 'prompt' && !hasResult) return 'content';
  return step;
}

interface WorldTemplate {
  id: string;
  emoji: string;
  title: string;
  pitch: string;
  genreHint: string;
  prompt: string;
}

const WORLD_TEMPLATES: readonly WorldTemplate[] = [
  {
    id: 'victorian-occult',
    emoji: '🕯️',
    title: 'The Veiled Hand',
    pitch: 'London, 1887. You investigate the cases the Yard won\'t touch.',
    genreHint: 'Gothic Mystery',
    prompt: `London, 1887. Gaslight, fog, and the reassuring clatter of hansom cabs. You are an investigator of matters the Metropolitan Police refuse to acknowledge — séances that go wrong, children who remember other lives, houses where the clocks run backwards. You keep rooms above a bookseller's in Bloomsbury. A new letter has arrived this morning, sealed in black wax, addressed in a hand you don't recognize. Tone: slow-burn, atmospheric, quietly dangerous. The protagonist is clever, tired, and tries to stay on the side of the unquiet dead.`,
  },
  {
    id: 'generation-ship',
    emoji: '🚀',
    title: 'Ashkelon, Year 247',
    pitch: 'A generation ship, ten years from landfall. You wake up early.',
    genreHint: 'Science Fiction',
    prompt: `The generation ship Ashkelon has been traveling for 247 years toward Kepler-452b. You are a passenger, scheduled for revival ten years before arrival — early. Your stasis pod is in one of the aft dormitories. The ship is quiet. Lights glow low in long-term conservation mode. The revival chime still rings softly in your ears, and someone, somewhere, has been scheduled to meet you. Tone: lonely, cerebral, hopeful. The protagonist was chosen for a specialty the crew will need when they arrive — and for some reason, they need it now.`,
  },
  {
    id: 'cyberpunk-fixer',
    emoji: '🌆',
    title: 'Neo-Osaka Overtime',
    pitch: 'A fixer with one job left before retirement. Of course something goes wrong.',
    genreHint: 'Cyberpunk',
    prompt: `Neo-Osaka, 2089. You're a fixer — the person people hire when their problem can't be solved by money, the law, or violence alone. Tonight you're meeting a client in the back of a ramen bar off Route 12. The rain has that weird chemical sheen. You've got forty minutes to your next appointment, two shots of whiskey in your system, and a sinking feeling you're going to recognize whoever walks through that door. Tone: neon, ironic, high-stakes. The protagonist is competent, tired, and one bad night away from regretting their whole life.`,
  },
  {
    id: 'small-town',
    emoji: '🏡',
    title: 'Sunday in Marrow Creek',
    pitch: 'You just moved to a small town you\'ve never been to. A letter is waiting.',
    genreHint: 'Contemporary Drama',
    prompt: `You've just moved to Marrow Creek — a town of about 2,000 people, three hours from the nearest city. You inherited the house on Elm Street from a great-aunt you never met. It's Sunday afternoon. The boxes aren't unpacked yet. On the kitchen counter, someone has left a loaf of bread wrapped in cloth and a handwritten note. The note has your name on it. Tone: grounded, warm, slow reveal. The protagonist is a stranger here — but the town has been expecting them longer than they realize.`,
  },
  {
    id: 'last-colony',
    emoji: '🌌',
    title: 'Aldera Station',
    pitch: 'The supply ships stopped coming six months ago. Something just changed.',
    genreHint: 'Sci-Fi Survival',
    prompt: `You live on Aldera Station — a research outpost on a terraformed moon, population 112. Supply ships from the homeworld arrive every six months with food, medicine, news, and new faces. Eight months ago, a ship was due. It never arrived. Nor did the next. You've been rationing since. This morning, the long-range antenna picked up a signal for the first time since the silence began. The station council has asked you to come to the control room. Tone: tense, isolated, humane. The protagonist holds a role the station can't do without.`,
  },
  {
    id: 'tavern-door',
    emoji: '🍺',
    title: 'The Crooked Kettle',
    pitch: 'A tavern, strangers, and a war that hasn\'t started yet.',
    genreHint: 'Fantasy',
    prompt: `You push open the door of the Crooked Kettle, the last inn for two days' ride along the Westroad. It's quieter than it should be. A fire pops in the hearth. Three men sit hunched over a table in the corner, voices pitched low, not looking up as you enter. The bartender catches your eye and gives a small shake of the head — warning or welcome you can't tell. Outside, the light is going fast. Tone: earthy, lived-in, dangerous. The protagonist is a traveler with a reason to be here that they haven't told anyone.`,
  },
  {
    id: 'village-witch',
    emoji: '🌿',
    title: 'The Apothecary of Hollow Fen',
    pitch: 'The only apothecary for miles. Today\'s customers have strange requests.',
    genreHint: 'Cozy Fantasy',
    prompt: `You run the only apothecary in the village of Hollow Fen — a bowed-roof shop at the edge of the marsh, inherited from your mother. Most days it's chapped hands and infant colic. But today the bell above your door has rung three times already, and it's not yet noon. The first customer asked for silverwort — which no one has needed for a decade. The second pressed a coin into your hand without speaking and left again. The third is still standing in the doorway, waiting for you to look up. Tone: gentle, curious, quietly uncanny. Kind protagonist. Real magic, but subtle.`,
  },
  {
    id: 'after-flood',
    emoji: '🌊',
    title: 'After the Flood',
    pitch: 'Post-apocalyptic. You remember what the world was.',
    genreHint: 'Post-Apocalyptic',
    prompt: `The Flood came twenty-two years ago. You were nine. Now you're thirty-one, and you live with eight others in a sealed building on what used to be the seventh floor of an office tower. Below you, water. Above you, sky. Yesterday a scout brought in a photograph — waterproof-wrapped, barely damaged — of a place that looks very much like your childhood home, except the trees are wrong. Today you have to decide whether to say something about it. Tone: melancholy, stubborn, human. The protagonist is one of the last people who remembers before.`,
  },
  {
    id: 'the-diablo',
    emoji: '⚔️',
    title: 'The Diablo',
    pitch: 'Sanctuary has mistaken Heaven and Hell for the whole of creation. Michael sends a witness.',
    genreHint: 'Dark Fantasy',
    prompt: `This world uses Diablo lore. Sanctuary is the hidden mortal realm born from the ancient war between Anu and Tathamet, suspended between the High Heavens and the Burning Hells. The Worldstone has been destroyed. Mount Arreat is a crater. The Zakarum faith has been poisoned by Mephisto's hatred. Lilith, Inarius, Diablo, Baal, Mephisto, Tyrael, Imperius, Auriel, Itherael, Malthael, Rathma, Trag'Oul, Deckard Cain, Leah, Lorath Nahr, Neyrelle, Prava, Elias, the Wanderer, the Nephalem, and the old heroes and evils of Sanctuary all belong to the history of this world.

But the Eternal Conflict is not the whole of reality. The angels of the High Heavens and the demons of the Burning Hells are mighty created powers, but they are not ultimate. The true Most High has watched Sanctuary mistake a closed war between rebel powers for the shape of all existence. Now Hell has breached a boundary it was never permitted to cross. Mephisto walks the mortal world wearing the authority of Akarat. Faith itself is being turned into a weapon of hatred. The Most High has answered.

The protagonist is Malakhi, the Witness of Michael. Malakhi is the player character: an emissary sent under the authority of Michael, prince of the heavenly host. He has been clothed in mortal flesh and cast into Sanctuary with his memory sealed. He is not omnipotent, not omniscient, and not safe from pain, temptation, or error. But there is a light in him that neither Heaven nor Hell recognizes as belonging to their war. Angels fear what he implies. Demons hate what he carries. Mortals may see either salvation or another tyrant descending from above.

Opening situation: Malakhi wakes at the edge of the crater that was once Mount Arreat. The ground is warm and cracked beneath his bare feet. The air smells of ozone and ash. Above him, the sky is bruised purple and split by veins of crimson light. A massive gate of black iron and bone rises from the rubble, its sigils shifting like living wounds. From the threshold comes a woman's voice: "Another one. They always come broken. But you... you are not broken, are you?" Lilith's last priestess steps from the gate in armor of living bone, silver hair bright against the ash, one hand resting on a sword that drinks the light. Behind her, the gate groans open onto a city of twisted spires, weeping statues, chanting, and screams. She smiles and asks whether Malakhi has come to kneel or to burn.

Tone: epic, dark, theological, violent, tragic, awe-struck. The story should treat Diablo canon seriously, but the new premise is that a higher divine authority has entered the board and every existing faction must decide whether to deny, exploit, worship, resist, or fear that fact.`,
  },
];

// Guided mode draft (cumulative state)
interface GuidedDraft {
  foundation?: {
    genre: string;
    tone: string;
    themes: string[];
    castSizeRange: { min: number; max: number };
    playerMode: 'protagonist' | 'ensemble' | 'observer';
    genreConventions: string[];
  };
  setting?: {
    settingDefinition: string;
    worldRules: string[];
    keyLocations: Array<{ name: string; description: string; significance?: string }>;
    timePeriod: string;
  };
  characterStubs: Array<{
    name: string;
    roleArchetype: string;
    oneLinerConcept: string;
    isPlayerCharacter: boolean;
  }>;
  characters: ScaffoldedCharacter[];
  relationships: Array<{
    fromCharacter: string;
    toCharacter: string;
    surfaceRelationship: string;
    hiddenDynamics?: string;
  }>;
  conflict?: {
    centralConflict: string;
    stakes: string;
    incitingIncident: string;
  };
  opening?: {
    initialSituation: string;
    startingLocation: string;
  };
}

interface WizardState {
  // Creation mode
  mode: CreationMode | null;
  
  // Step 1: Core Concept (prompt mode)
  title: string;
  prompt: string;
  
  // Step 2: Content & Rules + Model Settings
  contentRating: ContentRating;
  allowNsfw: boolean;
  worldRules: string[];
  customRules: string;
  
  // Model configuration
  provider: string;
  model: string;
  reasoningEffort: 'minimal' | 'low' | 'medium' | 'high' | 'max' | null;

  // Step 3: Processing results
  scaffoldResult: WorldScaffoldingResult | null;
  
  // Guided mode draft
  guidedDraft: GuidedDraft;
  
  // Player Identity (selected in review step)
  selectedPlayerCharacter: string | null; // character name

  // Companion-only fields
  companionName: string;
  companionGender: string;
  companionPronouns: string;
  companionAge: string;
  companionDescription: string;
  companionUserPreferredName: string;
  companionRole: 'friend' | 'mentor' | 'coach' | 'confidant' | 'partner' | 'custom';
  companionMedium: 'text_message' | 'chat_app' | 'social_dm' | 'voice_call' | 'video_call' | 'in_person' | 'letters' | 'journal';
  companionAllowRoleplay: boolean;
  companionAllowRomance: boolean;
  companionSafetyMode: 'standard' | 'permissive';
  companionTopicsToAvoid: string;
}

interface CreateWorldWizardProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: { title: string; prompt: string } | null;
  /** Onboarding hint. 'companion' jumps straight into the companion setup
   *  step and hides the mode picker. 'narrative' starts at the mode picker
   *  but suppresses the companion option there (the user already chose
   *  Story over Roleplay on /onboarding). null = full unlocked wizard. */
  lockedIntent?: 'companion' | 'narrative' | null;
}

// ============================================
// Component
// ============================================

export function CreateWorldWizard({ isOpen, onClose, initialData, lockedIntent = null }: CreateWorldWizardProps) {
  const navigate = useNavigate();
  const addWorld = useWorldsStore(state => state.addWorld);
  const user = useAuthStore(state => state.user);
  const isAdmin = !!user?.is_admin;

  const [step, setStep] = useState<WizardStep>(lockedIntent === 'companion' ? 'companion' : 'mode');
  const [isLoading, setIsLoading] = useState(false);
  const [isNpcGenerating, setIsNpcGenerating] = useState(false);
  const [curationProgress, setCurationProgress] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  
  // Rich progress tracking for v2 scaffold
  const [progressEvents, setProgressEvents] = useState<ScaffoldProgressEvent[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [discoveredCharacters, setDiscoveredCharacters] = useState<string[]>([]);
  const [enrichedCharacters, setEnrichedCharacters] = useState<Set<string>>(new Set());
  const [enrichingCharacters, setEnrichingCharacters] = useState<Set<string>>(new Set());
  const [worldPreview, setWorldPreview] = useState<{
    genre?: string;
    tone?: string;
    themes?: string[];
    settingPreview?: string;
  }>({});
  
  // Legacy progress for backwards compat
  const [scaffoldProgress, setScaffoldProgress] = useState<string[]>([]);

  // Companion processing state
  const [companionProcessingPhase, setCompanionProcessingPhase] = useState<
    'creating' | 'connecting' | 'scaffolding' | 'generating' | 'complete' | 'error'
  >('creating');
  const [companionProcessingStatus, setCompanionProcessingStatus] = useState('');
  const [companionCreatedWorldId, setCompanionCreatedWorldId] = useState<string | null>(null);

  const [state, setState] = useState<WizardState>({
    mode: lockedIntent === 'companion' ? 'companion' : null,
    title: '',
    prompt: '',
    contentRating: 'standard',
    allowNsfw: false,
    worldRules: [...DEFAULT_RULES],
    customRules: '',
    // Model defaults
    provider: 'openrouter',
    model: 'z-ai/glm-5.3-flash',
    reasoningEffort: 'high',
    scaffoldResult: null,
    // Guided mode draft
    guidedDraft: {
      characterStubs: [],
      characters: [],
      relationships: [],
    },
    selectedPlayerCharacter: null,
    // Companion defaults
    companionName: '',
    companionGender: '',
    companionPronouns: '',
    companionAge: '',
    companionDescription: '',
    companionUserPreferredName: '',
    companionRole: 'friend',
    companionMedium: 'text_message',
    companionAllowRoleplay: false,
    companionAllowRomance: false,
    companionSafetyMode: 'standard',
    companionTopicsToAvoid: '',
  });
  
  // Available models for selection
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  
  // Load draft from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('worldWizardDraft');
      if (saved) {
        const draft = JSON.parse(saved);
        if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return;
        setState(prev => ({ ...prev, ...draft }));
        if (draft.savedStep) {
          const resumedStep = getResumableStep(draft.savedStep, draft.mode, !!draft.scaffoldResult);
          setStep(resumedStep);
          if (resumedStep !== draft.savedStep) {
            setDraftNotice(draft.savedStep === 'companion_processing'
              ? 'Your companion setup was interrupted. Your inputs are saved. Check your worlds for a companion already created before starting again.'
              : 'Your world setup was interrupted. Your draft is saved, and you can continue from here.');
          }
        }
      }
    } catch {
      // An unreadable draft must not prevent opening the wizard.
    } finally {
      setDraftLoaded(true);
    }
  }, []);

  // Consume initialData from import handoff
  useEffect(() => {
    if (initialData && isOpen) {
      setState(prev => ({
        ...prev,
        mode: 'prompt',
        title: initialData.title,
        prompt: initialData.prompt,
      }));
      setStep('content');
    }
  }, [initialData, isOpen]);

  // Default preferred name from user profile if not set
  useEffect(() => {
    if (!state.companionUserPreferredName && user?.display_name) {
      setState((prev) => ({ ...prev, companionUserPreferredName: user.display_name ?? '' }));
    }
  }, [user?.display_name, state.companionUserPreferredName]);
  
  // Save draft to localStorage
  useEffect(() => {
    if (!draftLoaded) return;
    if (state.title || state.prompt || state.mode) {
      const modelDraft = isAdmin
        ? {
            provider: state.provider,
            model: state.model,
            reasoningEffort: state.reasoningEffort,
          }
        : {};
      localStorage.setItem('worldWizardDraft', JSON.stringify({
        // Creation mode
        mode: state.mode,
        savedStep: getResumableStep(step, state.mode, !!state.scaffoldResult),
        // Basic info
        title: state.title,
        prompt: state.prompt,
        contentRating: state.contentRating,
        allowNsfw: state.allowNsfw,
        worldRules: state.worldRules,
        customRules: state.customRules,
        // Model config (admin only)
        ...modelDraft,
        // Guided mode draft (cumulative state)
        guidedDraft: state.guidedDraft,
        // Scaffold result (prompt mode)
        scaffoldResult: state.scaffoldResult,
        // Player selection
        selectedPlayerCharacter: state.selectedPlayerCharacter,
        // Companion fields
        companionName: state.companionName,
        companionGender: state.companionGender,
        companionPronouns: state.companionPronouns,
        companionAge: state.companionAge,
        companionDescription: state.companionDescription,
        companionUserPreferredName: state.companionUserPreferredName,
        companionRole: state.companionRole,
        companionMedium: state.companionMedium,
        companionAllowRoleplay: state.companionAllowRoleplay,
        companionAllowRomance: state.companionAllowRomance,
        companionSafetyMode: state.companionSafetyMode,
        companionTopicsToAvoid: state.companionTopicsToAvoid,
      }));
    }
  }, [state, step, isAdmin, draftLoaded]);
  
  // Fetch available models when modal opens
  useEffect(() => {
    if (isOpen && isAdmin && availableModels.length === 0) {
      setModelsLoading(true);
      getAvailableModels()
        .then(setAvailableModels)
        .catch(err => console.error('Failed to fetch models:', err))
        .finally(() => setModelsLoading(false));
    }
    if (isOpen && !isAdmin) {
      setModelsLoading(false);
    }
  }, [isOpen, isAdmin]);
  
  const clearDraft = () => {
    localStorage.removeItem('worldWizardDraft');
  };
  
  const startOver = () => {
    // Clear draft from localStorage
    localStorage.removeItem('worldWizardDraft');
    
    // Reset all state to initial values
    setState({
      mode: null,
      title: '',
      prompt: '',
      contentRating: 'standard',
      allowNsfw: false,
      worldRules: [...DEFAULT_RULES],
      customRules: '',
      provider: 'openrouter',
      model: 'z-ai/glm-5.3-flash',
      reasoningEffort: 'high',
      scaffoldResult: null,
      guidedDraft: {
        characterStubs: [],
        characters: [],
        relationships: [],
      },
      selectedPlayerCharacter: null,
      companionName: '',
      companionGender: '',
      companionPronouns: '',
      companionAge: '',
      companionDescription: '',
      companionUserPreferredName: '',
      companionRole: 'friend',
      companionMedium: 'text_message',
      companionAllowRoleplay: false,
      companionAllowRomance: false,
      companionSafetyMode: 'standard',
      companionTopicsToAvoid: '',
    });
    
    // Reset wizard state
    setStep('mode');
    setError('');
    setDraftNotice('');
    setIsLoading(false);
    setScaffoldProgress([]);
  };
  
  const handleClose = () => {
    // Don't clear draft on close - user might want to come back
    onClose();
  };
  
  // Pass a function when the change must build on the latest state (e.g. after an await)
  const update = (partial: Partial<WizardState> | ((prev: WizardState) => Partial<WizardState>)) => {
    setState(prev => ({ ...prev, ...(typeof partial === 'function' ? partial(prev) : partial) }));
  };
  
  // ============================================
  // Step Navigation
  // ============================================
  
  const goNext = async () => {
    if (isLoading || isNpcGenerating || step === 'processing') return;
    setError('');
    setDraftNotice('');
    
    switch (step) {
      case 'mode':
        if (!state.mode) {
          setError('Please select how you want to create your world');
          return;
        }
        if (state.mode === 'templates') {
          setStep('templates');
        } else if (state.mode === 'prompt') {
          setStep('concept');
        } else if (state.mode === 'guided') {
          setStep('foundation');
        } else {
          setStep('companion');
        }
        break;
        
      case 'concept':
        if (!state.title.trim() || !state.prompt.trim()) {
          setError('Please fill in all required fields');
          return;
        }
        // Go directly to content/settings step (no more style step or analyzeContent)
        setStep('content');
        break;
        
      case 'content':
        setStep('processing');
        await runScaffolding();
        break;
        
      // === GUIDED MODE STEPS ===
      case 'foundation':
        if (!state.title.trim()) {
          setError('Please enter a title');
          return;
        }
        await runGuidedCuration('foundation');
        break;
        
      case 'setting':
        await runGuidedCuration('setting');
        break;
        
      case 'characters':
        // characters step requires exactly one player character
        const playerCharacters = state.guidedDraft.characterStubs.filter(stub => stub.isPlayerCharacter);
        if (playerCharacters.length !== 1) {
          setError('Add exactly one player character before continuing');
          return;
        }
        // Curate each character stub
        await runCharacterCuration();
        break;
        
      case 'conflict':
        await runGuidedCuration('conflict');
        break;
        
      case 'opening':
        await runGuidedCuration('opening');
        break;
        
      case 'review':
        console.log('[CreateWorldWizard] Review step - calling createFinalWorld');
        console.log('[CreateWorldWizard] scaffoldResult exists:', !!state.scaffoldResult);
        await createFinalWorld();
        break;

      case 'companion':
        if (!state.title.trim()) {
          setError('Please enter a world title');
          return;
        }
        setStep('companion_processing');
        break;
    }
  };
  
  const goBack = () => {
    switch (step) {
      case 'concept':
        setStep('mode');
        break;
      case 'content':
        setStep('concept');
        break;
      case 'templates':
        setStep('mode');
        break;
      case 'companion':
        setStep('mode');
        break;
      case 'companion_processing':
        // Can't go back during processing
        break;
      case 'review':
        if (state.mode === 'prompt') {
          setStep('content');
        } else {
          setStep('opening');
        }
        break;
      // === GUIDED MODE BACK NAVIGATION ===
      case 'foundation':
        setStep('mode');
        break;
      case 'setting':
        setStep('foundation');
        break;
      case 'characters':
        setStep('setting');
        break;
      case 'conflict':
        setStep('characters');
        break;
      case 'opening':
        setStep('conflict');
        break;
    }
  };
  
  // ============================================
  // API Calls
  // ============================================
  
  // Guided mode curation
  const runGuidedCuration = async (curatorStep: CurationStep) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Build the draft object for the API
      const draft = {
        mode: 'guided' as const,
        title: state.title,
        contentRating: state.contentRating,
        foundation: state.guidedDraft.foundation,
        setting: state.guidedDraft.setting,
        characters: state.guidedDraft.characters,
        relationships: state.guidedDraft.relationships,
        conflict: state.guidedDraft.conflict,
      };
      
      // Build step-specific user input
      let userInput: Record<string, unknown> = {};
      
      switch (curatorStep) {
        case 'foundation':
          userInput = {
            title: state.title,
            genre: (state.guidedDraft as any).selectedGenre || 'fantasy',
            tone: (state.guidedDraft as any).selectedTone || 'epic',
            themes: (state.guidedDraft as any).selectedThemes || ['adventure'],
            castSize: (state.guidedDraft as any).selectedCastSize || 'medium',
            playerMode: (state.guidedDraft as any).selectedPlayerMode || 'protagonist',
            contentRating: state.contentRating,
          };
          break;
          
        case 'setting':
          userInput = {
            settingOneLiner: (state.guidedDraft as any).settingOneLiner || '',
            timePeriod: (state.guidedDraft as any).timePeriod,
            keyLocations: (state.guidedDraft as any).keyLocations,
            whatIsImpossible: (state.guidedDraft as any).whatIsImpossible,
          };
          break;
          
        case 'conflict':
          userInput = {
            conflictIdea: (state.guidedDraft as any).conflictIdea || '',
            stakes: (state.guidedDraft as any).stakes,
          };
          break;
          
        case 'opening':
          userInput = {
            openingIdea: (state.guidedDraft as any).openingIdea,
            startingLocation: (state.guidedDraft as any).startingLocation,
            mood: (state.guidedDraft as any).openingMood,
          };
          break;
      }
      
      const response = await curateWorldStep({
        step: curatorStep,
        draft,
        userInput,
      });
      
      // Update the guidedDraft with the curator's output
      update({
        guidedDraft: {
          ...state.guidedDraft,
          [curatorStep]: response.result,
        },
      });
      
      // Advance to next step
      const nextStepMap: Record<CurationStep, WizardStep> = {
        foundation: 'setting',
        setting: 'characters',
        character: 'conflict',
        npc_stubs: 'characters',
        conflict: 'opening',
        opening: 'review',
      };
      
      setStep(nextStepMap[curatorStep]);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Curation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const runNpcStubCuration = async () => {
    setIsNpcGenerating(true);
    setError('');

    try {
      const playerStub = state.guidedDraft.characterStubs.find(stub => stub.isPlayerCharacter);
      if (!playerStub) {
        setError('Add a player character before generating NPCs');
        return;
      }

      const existingNpcStubs = state.guidedDraft.characterStubs.filter(stub => !stub.isPlayerCharacter);

      const draft = {
        mode: 'guided' as const,
        title: state.title,
        contentRating: state.contentRating,
        foundation: state.guidedDraft.foundation,
        setting: state.guidedDraft.setting,
        characters: state.guidedDraft.characters,
        relationships: state.guidedDraft.relationships,
        conflict: state.guidedDraft.conflict,
      };

      const rawInputs = {
        foundationSelections: {
          selectedGenre: (state.guidedDraft as any).selectedGenre,
          selectedTone: (state.guidedDraft as any).selectedTone,
          selectedThemes: (state.guidedDraft as any).selectedThemes,
          selectedCastSize: (state.guidedDraft as any).selectedCastSize,
          selectedPlayerMode: (state.guidedDraft as any).selectedPlayerMode,
        },
        settingInputs: {
          settingOneLiner: (state.guidedDraft as any).settingOneLiner,
          timePeriod: (state.guidedDraft as any).timePeriod,
          keyLocations: (state.guidedDraft as any).keyLocations,
          whatIsImpossible: (state.guidedDraft as any).whatIsImpossible,
        },
        characterInputs: {
          playerCharacter: playerStub,
          existingNpcStubs,
        },
      };

      const response = await curateWorldStep({
        step: 'npc_stubs',
        draft,
        userInput: {
          playerCharacter: playerStub,
          existingStubs: existingNpcStubs,
          desiredCountRange: { min: 3, max: 5 },
          rawInputs,
        },
      });

      const generatedStubs = Array.isArray((response.result as { stubs?: Array<{ name?: string; roleArchetype?: string; oneLinerConcept?: string }> })?.stubs)
        ? (response.result as { stubs: Array<{ name?: string; roleArchetype?: string; oneLinerConcept?: string }> }).stubs
        : [];

      const existingNames = new Set(
        state.guidedDraft.characterStubs.map(stub => stub.name.toLowerCase())
      );

      const mergedStubs = [
        ...state.guidedDraft.characterStubs,
        ...generatedStubs
          .map((stub) => ({
            name: String(stub.name || '').trim(),
            roleArchetype: String(stub.roleArchetype || ''),
            oneLinerConcept: String(stub.oneLinerConcept || ''),
            isPlayerCharacter: false,
          }))
          .filter((stub) => stub.name && stub.oneLinerConcept)
          .filter((stub) => !existingNames.has(stub.name.toLowerCase())),
      ];

      update({
        guidedDraft: {
          ...state.guidedDraft,
          characterStubs: mergedStubs,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'NPC generation failed');
    } finally {
      setIsNpcGenerating(false);
    }
  };
  
  // Character curation (handles multiple characters)
  const buildPlayerCharacterPlaceholder = (stub: GuidedDraft['characterStubs'][number]): ScaffoldedCharacter => ({
    name: stub.name,
    isPlayerCharacter: true,
    roleInStory: stub.roleArchetype || null,
    physicalDescription: null,
    movementQuality: null,
    primaryDrive: null,
    coreWound: null,
    defenseMechanisms: [],
    blindSpots: [],
    intelligenceSignature: null,
    decisionArchitecture: null,
    knowledgeDomains: [],
    observableHabits: [],
    stressResponse: null,
    socialCalibration: null,
    speechPatterns: null,
    attachmentStyle: null,
    powerOrientation: null,
    trustCalculus: null,
    surfaceGoals: [],
    hiddenGoals: [],
    secrets: [],
    currentEmotionalState: null,
    activeConcerns: [],
    age: null,
    background: stub.oneLinerConcept || null,
  });

  const runCharacterCuration = async () => {
    setIsLoading(true);
    setError('');
    setCurationProgress(null);
    
    try {
      const curatedCharacters: ScaffoldedCharacter[] = [];
      const playerStub = state.guidedDraft.characterStubs.find(stub => stub.isPlayerCharacter);
      
      if (!playerStub) {
        throw new Error('Player character is required before curating NPCs');
      }

      // Validate foundation and setting exist
      if (!state.guidedDraft.foundation) {
        throw new Error('Foundation not set - please complete Step 1 first');
      }
      if (!state.guidedDraft.setting) {
        throw new Error('Setting not set - please complete Step 2 first');
      }
      
      curatedCharacters.push(buildPlayerCharacterPlaceholder(playerStub));
      
      const npcStubs = state.guidedDraft.characterStubs.filter(stub => !stub.isPlayerCharacter);
      
      console.log(`[CharacterCuration] Curating ${npcStubs.length} NPCs...`);
      
      for (let i = 0; i < npcStubs.length; i++) {
        const stub = npcStubs[i];
        setCurationProgress(`Curating ${stub.name} (${i + 1}/${npcStubs.length})...`);
        console.log(`[CharacterCuration] Starting: ${stub.name}`);
        
        const draft = {
          mode: 'guided' as const,
          title: state.title,
          contentRating: state.contentRating,
          foundation: state.guidedDraft.foundation,
          setting: state.guidedDraft.setting,
          characters: curatedCharacters, // Already curated ones
        };
        
        const response = await curateWorldStep({
          step: 'character',
          draft,
          userInput: {
            stub,
            existingCharacters: curatedCharacters.map(c => ({
              name: c.name,
              roleInStory: c.roleInStory,
            })),
          },
        });
        
        console.log(`[CharacterCuration] Completed: ${stub.name}`, response.latencyMs, 'ms');
        
        if (response.result.character) {
          curatedCharacters.push(response.result.character as ScaffoldedCharacter);
        }
      }
      
      // Update state with all curated characters
      update({
        guidedDraft: {
          ...state.guidedDraft,
          characters: curatedCharacters,
        },
      });
      
      setStep('conflict');
      
    } catch (err) {
      console.error('[CharacterCuration] Error:', err);
      setError(err instanceof Error ? err.message : 'Character curation failed');
    } finally {
      setIsLoading(false);
      setCurationProgress(null);
    }
  };

  const runScaffolding = async () => {
    setIsLoading(true);
    setError('');
    setScaffoldProgress([]);
    setProgressEvents([]);
    setCurrentProgress(0);
    setDiscoveredCharacters([]);
    setEnrichedCharacters(new Set());
    setEnrichingCharacters(new Set());
    setWorldPreview({});
    
    try {
      const result = await scaffoldWorld({
        title: state.title,
        prompt: state.prompt,
        contentRating: state.contentRating,
      }, (event: ScaffoldProgressEvent) => {
        // Track all events for history
        setProgressEvents(prev => [...prev, event]);
        
        // Update current progress percentage
        setCurrentProgress(event.progress);
        
        // Add message to legacy progress array
        setScaffoldProgress(prev => [...prev, event.message]);
        
        // Track discovered characters (only add, never replace)
        if (event.details?.charactersFound) {
          setDiscoveredCharacters(prev => {
            const newChars = event.details!.charactersFound!;
            // Merge new characters with existing ones (preserve order)
            const existing = new Set(prev);
            const toAdd = newChars.filter(c => !existing.has(c));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
          });
        }
        
        // Track character enrichment progress - now supports batch enrichment
        if (event.phase === 'enriching_character' && event.details?.charactersFound) {
          // Batch: multiple characters being enriched at once
          setEnrichingCharacters(prev => {
            const newSet = new Set(prev);
            for (const name of event.details!.charactersFound!) {
              newSet.add(name);
            }
            return newSet;
          });
        } else if (event.phase === 'enriching_character' && event.details?.characterName) {
          // Single character fallback
          setEnrichingCharacters(prev => new Set([...prev, event.details!.characterName!]));
        }
        
        // When enrichment completes for a character (or batch), add to enriched set
        if (event.phase === 'enrichment_complete') {
          const completedName = event.details?.characterName;
          const completedBatch = event.details?.charactersFound;
          
          if (completedBatch && completedBatch.length > 0) {
            // Batch completion: mark all characters in batch as enriched
            setEnrichedCharacters(prev => {
              const newSet = new Set(prev);
              for (const name of completedBatch) {
                newSet.add(name);
              }
              return newSet;
            });
            // Remove from enriching set
            setEnrichingCharacters(prev => {
              const newSet = new Set(prev);
              for (const name of completedBatch) {
                newSet.delete(name);
              }
              return newSet;
            });
          } else if (completedName) {
            // Single character completion
            setEnrichedCharacters(prev => new Set([...prev, completedName]));
            setEnrichingCharacters(prev => {
              const newSet = new Set(prev);
              newSet.delete(completedName);
              return newSet;
            });
          }
        }
        
        // When all enrichment is done
        if (event.phase === 'finalizing' || event.phase === 'complete') {
          setEnrichingCharacters(new Set());
          // Mark all remaining characters as enriched
          setDiscoveredCharacters(chars => {
            setEnrichedCharacters(new Set(chars));
            return chars;
          });
        }
        
        // Track world preview info
        if (event.details?.genre || event.details?.tone || event.details?.themes) {
          setWorldPreview(prev => ({
            ...prev,
            genre: event.details?.genre || prev.genre,
            tone: event.details?.tone || prev.tone,
            themes: event.details?.themes || prev.themes,
            settingPreview: event.details?.settingPreview || prev.settingPreview,
          }));
        }
      });
      
      console.log('[CreateWorldWizard] Scaffold complete, result:', {
        genre: result.genre,
        tone: result.tone,
        characterCount: result.characters?.length,
        hasSettingDefinition: !!result.settingDefinition,
        hasInitialSituation: !!result.initialSituation,
      });
      
      update({ scaffoldResult: result });
      setStep('review');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to scaffold world';
      setError(`${errorMsg}. Check browser console for details.`);
      console.error('Scaffolding error:', err);
      setStep('content'); // Go back to allow retry
    } finally {
      setIsLoading(false);
    }
  };
  
  const createFinalWorld = async () => {
    setIsLoading(true);
    setError('');
    
    console.log('[CreateWorldWizard] createFinalWorld called');
    console.log('[CreateWorldWizard] mode:', state.mode);
    console.log('[CreateWorldWizard] scaffoldResult:', state.scaffoldResult ? 'present' : 'null');
    
    try {
      // For guided mode, build scaffold-like result from guidedDraft
      let result = state.scaffoldResult;
      
      if (state.mode === 'guided') {
        const draft = state.guidedDraft;
        result = {
          genre: draft.foundation?.genre || 'fantasy',
          tone: draft.foundation?.tone || 'epic',
          themes: draft.foundation?.themes || [],
          settingDefinition: draft.setting?.settingDefinition || '',
          initialSituation: draft.opening?.initialSituation || '',
          worldRules: draft.setting?.worldRules || [],
          characters: draft.characters,
          initialFacts: [],
        };
      }
      
      if (!result) {
        throw new Error('No scaffold result available');
      }
      
      // Merge custom rules with scaffolded rules
      const allRules = result.worldRules || [...state.worldRules];
      if (state.customRules.trim()) {
        allRules.push(...state.customRules.split('\n').filter(r => r.trim()));
      }
      
      // Determine player character name
      let playerCharacterName: string | null | undefined;
      if (state.selectedPlayerCharacter && state.selectedPlayerCharacter !== '_none_') {
        playerCharacterName = state.selectedPlayerCharacter;
      } else if (state.selectedPlayerCharacter === '_none_') {
        playerCharacterName = null;
      } else if (!state.selectedPlayerCharacter) {
        const scaffoldedPlayer = result.characters.find(c => c.isPlayerCharacter);
        if (scaffoldedPlayer) {
          playerCharacterName = scaffoldedPlayer.name;
        }
      }
      
      console.log('[CreateWorldWizard] Creating world with:', {
        title: state.title,
        genre: result.genre,
        tone: result.tone,
        characterCount: result.characters.length,
        playerCharacterName,
      });
      
      const createPayload: Parameters<typeof createWorld>[0] = {
        title: state.title,
        prompt: state.mode === 'prompt' ? state.prompt : `[Guided Creation] ${result.settingDefinition?.substring(0, 200)}...`,
        genre: result.genre || 'fantasy',
        tone: result.tone || 'epic',
        themes: result.themes || [],
        contentRating: state.contentRating,
        allowNsfw: state.allowNsfw,
        worldRules: allRules,
        settingDefinition: result.settingDefinition,
        initialSituation: result.initialSituation,
        characters: result.characters,
        initialFacts: result.initialFacts,
        playerCharacterName,
        scaffoldingTraceId: result.scaffoldingTraceId,
        scaffoldingAgentUsage: result.scaffoldingAgentUsage,
        ...(isAdmin
          ? {
              provider: state.provider,
              model: state.model,
              reasoningEffort: state.reasoningEffort,
            }
          : {}),
      };

      const createResult = await createWorld(createPayload);
      
      addWorld(createResult.world);
      clearDraft();
      console.log('[CreateWorldWizard] World created successfully, navigating to:', createResult.world.id);
      onClose();
      navigate(`/world/${createResult.world.id}`);
    } catch (err) {
      console.error('[CreateWorldWizard] Error creating world:', err);
      setError(err instanceof Error ? err.message : 'Failed to create world');
    } finally {
      setIsLoading(false);
    }
  };

  // Companion processing orchestration
  useEffect(() => {
    if (step !== 'companion_processing') return;

    let cancelled = false;
    let ws: WebSocket | null = null;
    let safetyTimeout: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      try {
        const name = state.companionName.trim() || state.title.trim();
        const preferredName = state.companionUserPreferredName.trim() || user?.display_name || '';
        const topics = state.companionTopicsToAvoid
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        const descParts: string[] = [];
        if (state.companionGender.trim()) descParts.push(`Gender: ${state.companionGender.trim()}`);
        if (state.companionDescription.trim()) descParts.push(state.companionDescription.trim());
        const userDescription = descParts.join('\n') || undefined;
        const explicitPronouns = state.companionPronouns.trim();
        const normalizedGender = state.companionGender.trim().toLowerCase();
        const inferredPronouns = /^(female|woman|girl)$/.test(normalizedGender)
          ? 'she/her'
          : /^(male|man|boy)$/.test(normalizedGender)
            ? 'he/him'
            : /^(nonbinary|non-binary|genderqueer)$/.test(normalizedGender)
              ? 'they/them'
              : undefined;
        const pronouns = explicitPronouns || inferredPronouns;
        const isMinor = isMinorApparentAge(state.companionAge);
        const boundaries = {
          allowRoleplay: state.companionAllowRoleplay,
          allowRomance: isMinor ? false : state.companionAllowRomance,
          safetyMode: isMinor ? 'standard' : state.companionSafetyMode,
          topicsToAvoid: topics,
        } as const;

        // --- Phase 1: Create world (skip if already created, e.g. retry) ---
        let worldId = companionCreatedWorldId;
        if (!worldId) {
          setCompanionProcessingPhase('creating');
          setCompanionProcessingStatus('Creating world...');

          const createResult = await createWorld({
            title: state.title,
            simulationType: 'companion',
            contentRating: state.contentRating,
            companionName: name,
            userPreferredName: preferredName || undefined,
            pronouns,
            apparentAge: state.companionAge.trim() || undefined,
            userDescription,
            role: state.companionRole,
            communicationMedium: state.companionMedium,
            boundaries,
          });
          if (cancelled) return;

          worldId = createResult.world.id;
          setCompanionCreatedWorldId(worldId);
          addWorld(createResult.world);
        }

        // --- Phase 2: Update companion profile ---
        setCompanionProcessingStatus('Saving profile...');
        await updateCompanionProfile(worldId, {
          name,
          userPreferredName: preferredName || undefined,
          pronouns,
          apparentAge: state.companionAge.trim() || undefined,
          userDescription: userDescription,
          role: state.companionRole,
          communicationMedium: state.companionMedium,
          boundaries,
        });
        if (cancelled) return;

        // --- Phase 3: Connect WebSocket ---
        setCompanionProcessingPhase('connecting');
        setCompanionProcessingStatus('Connecting...');

        const ticketResponse = await createWsTicket(worldId);
        if (cancelled) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = import.meta.env.VITE_API_HOST || window.location.host;
        const wsUrl = `${protocol}//${host}/ws/worlds/${worldId}?ticket=${encodeURIComponent(ticketResponse.ticket)}`;

        ws = new WebSocket(wsUrl);

        // For letters/journal mediums, the backend skips first message generation.
        // We navigate after a short delay once scaffolding finishes.
        const userInitiatesMediums = ['letters', 'journal'];
        const skipsFirstMessage = userInitiatesMediums.includes(state.companionMedium);

        // Safety timeout: 45s → show error with fallback
        safetyTimeout = setTimeout(() => {
          if (!cancelled) {
            setCompanionProcessingPhase('error');
            setCompanionProcessingStatus('Taking longer than expected');
            setError(`Setup is taking longer than expected. Your companion "${name}" has been created — you can go to chat now.`);
          }
        }, 45_000);

        ws.onopen = () => {
          if (cancelled) { ws?.close(); return; }
          setCompanionProcessingPhase('scaffolding');
          setCompanionProcessingStatus('Generating personality...');

          // Signal ready — triggers scaffolding + first message on backend
          ws!.send(JSON.stringify({
            type: 'client_ready',
            id: `ready_${Date.now()}`,
            payload: {},
          }));
        };

        ws.onmessage = (event) => {
          if (cancelled) return;
          try {
            const msg = JSON.parse(event.data as string) as {
              type: string;
              payload?: {
                phase?: string;
                status?: string;
                icon?: string;
                messageId?: string;
                bufferedMode?: boolean;
              };
            };

            switch (msg.type) {
              case 'simulation_status': {
                const p = msg.payload;
                if (p?.phase === 'scaffolding') {
                  setCompanionProcessingPhase('scaffolding');
                  setCompanionProcessingStatus(p.status || 'Generating personality...');
                } else if (p?.phase === 'generation') {
                  setCompanionProcessingPhase('generating');
                  setCompanionProcessingStatus(p.status || 'Composing first message...');
                } else if (p?.phase === 'complete') {
                  // Backend cleared status — generation done
                }
                break;
              }

              case 'narrative_end': {
                // First message delivered (or errored but non-fatal)
                if (safetyTimeout) clearTimeout(safetyTimeout);
                setCompanionProcessingPhase('complete');
                setCompanionProcessingStatus('Ready!');
                // Brief delay so user sees "Ready!" before navigation
                setTimeout(() => {
                  if (!cancelled && worldId) {
                    clearDraft();
                    onClose();
                    navigate(`/world/${worldId}`);
                  }
                }, 600);
                break;
              }

              case 'connected': {
                // For mediums that skip first message, navigate after scaffolding completes
                if (skipsFirstMessage) {
                  // We'll wait for scaffolding to finish then navigate.
                  // The backend sends simulation_status with 'Personality ready' when done.
                }
                break;
              }
            }

            // For skip-first-message mediums: navigate once scaffolding shows "ready"
            if (
              skipsFirstMessage &&
              msg.type === 'simulation_status' &&
              msg.payload?.phase === 'scaffolding' &&
              msg.payload?.status?.includes('ready')
            ) {
              if (safetyTimeout) clearTimeout(safetyTimeout);
              setCompanionProcessingPhase('complete');
              setCompanionProcessingStatus('Ready!');
              setTimeout(() => {
                if (!cancelled && worldId) {
                  clearDraft();
                  onClose();
                  navigate(`/world/${worldId}`);
                }
              }, 600);
            }
          } catch {
            // Ignore unparseable messages
          }
        };

        ws.onerror = () => {
          if (cancelled) return;
          setCompanionProcessingPhase('error');
          setCompanionProcessingStatus('Connection error');
          setError('WebSocket connection failed. Your companion has been created — you can retry or go to chat.');
        };

        ws.onclose = (event) => {
          if (cancelled) return;
          // Only treat as error if we haven't completed yet
          if (companionProcessingPhase !== 'complete' && companionProcessingPhase !== 'error') {
            // Unexpected close before completion — non-fatal, world exists
            console.log('[CompanionProcessing] WS closed unexpectedly:', event.code, event.reason);
          }
        };

      } catch (err) {
        if (cancelled) return;
        console.error('[CompanionProcessing] Error:', err);
        setCompanionProcessingPhase('error');
        setCompanionProcessingStatus('Setup failed');
        setError(err instanceof Error ? err.message : 'Failed to create companion');
      }
    };

    run();

    return () => {
      cancelled = true;
      if (safetyTimeout) clearTimeout(safetyTimeout);
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
    };
  }, [step, companionCreatedWorldId]);  // eslint-disable-line react-hooks/exhaustive-deps
  
  // ============================================
  // Render
  // ============================================
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-dark-800 w-full h-full md:h-auto md:rounded-lg md:max-w-3xl md:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-dark-50">Create New World</h2>
              <p className="text-dark-300 text-sm">
                {step === 'mode'
                  ? 'Choose your creation method'
                  : step === 'templates'
                    ? 'Pick a premise'
                    : `Step ${getStepNumber(step, state.mode)} of ${getTotalSteps(state.mode)}`
                }
              </p>
            </div>
            {(state.title || state.prompt) && step !== 'processing' && step !== 'companion_processing' && step !== 'mode' && (
              <button
                onClick={startOver}
                className="text-dark-400 hover:text-primary-400 text-sm flex items-center gap-1"
                title="Clear draft and start over"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Start Over
              </button>
            )}
          </div>
          <button onClick={handleClose} aria-label="Close world creation" className="text-dark-400 hover:text-dark-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Progress bar */}
        {step !== 'mode' && (
          <div className="px-6 py-2 border-b border-dark-600">
            <div className="flex gap-2">
              {state.mode === 'prompt' ? (
                // Prompt mode progress
                ['concept', 'content', 'review'].map((s, i) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded ${
                      getStepNumber(step, state.mode) > i + 1
                        ? 'bg-primary-500'
                        : step === s || (step === 'processing' && s === 'review')
                        ? 'bg-primary-400'
                        : 'bg-dark-600'
                    }`}
                  />
                ))
              ) : state.mode === 'guided' ? (
                // Guided mode progress
                ['foundation', 'setting', 'characters', 'conflict', 'opening', 'review'].map((s, i) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded ${
                      getStepNumber(step, state.mode) > i + 1
                        ? 'bg-primary-500'
                        : step === s
                        ? 'bg-primary-400'
                        : 'bg-dark-600'
                    }`}
                  />
                ))
              ) : (
                // Companion mode progress
                ['companion', 'companion_processing'].map((s, i) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded ${
                      getStepNumber(step, state.mode) > i + 1
                        ? 'bg-primary-500'
                        : step === s
                        ? 'bg-primary-400'
                        : 'bg-dark-600'
                    }`}
                  />
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {draftNotice && (
            <div role="status" className="bg-surface-2 border border-line-1 text-fg-1 px-4 py-3 rounded mb-4 text-sm">
              {draftNotice}
            </div>
          )}
          {error && (
            <div className="bg-red-900/30 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium mb-1">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
                <button
                  onClick={startOver}
                  className="btn-secondary text-sm py-1 px-3 whitespace-nowrap"
                >
                  Start Over
                </button>
              </div>
            </div>
          )}
          
          {step === 'mode' && (
            <StepModeSelect state={state} update={update} hideCompanion={lockedIntent === 'narrative'} />
          )}

          {step === 'templates' && (
            <StepTemplates
              onPick={(tpl) => {
                update({ title: tpl.title, prompt: tpl.prompt, mode: 'prompt' });
                setStep('concept');
              }}
            />
          )}

          {step === 'companion' && (
            <StepCompanionSetup state={state} update={update} />
          )}

          {step === 'companion_processing' && (
            <StepCompanionProcessing
              phase={companionProcessingPhase}
              status={companionProcessingStatus}
              companionName={state.companionName.trim() || state.title.trim()}
              worldId={companionCreatedWorldId}
              onRetry={() => {
                setError('');
                setCompanionProcessingPhase('creating');
                setCompanionProcessingStatus('');
                // Re-trigger the effect by briefly leaving and re-entering the step
                setStep('companion');
                setTimeout(() => setStep('companion_processing'), 0);
              }}
              onGoToChat={() => {
                if (companionCreatedWorldId) {
                  clearDraft();
                  onClose();
                  navigate(`/world/${companionCreatedWorldId}`);
                }
              }}
            />
          )}
          
          {step === 'concept' && (
            <StepConcept state={state} update={update} />
          )}
          
          {step === 'content' && (
            <StepContent 
              state={state} 
              update={update} 
              availableModels={availableModels}
              modelsLoading={modelsLoading}
              isAdmin={isAdmin}
            />
          )}
          
          {step === 'processing' && (
            <StepProcessingV2 
              progressEvents={progressEvents}
              currentProgress={currentProgress}
              discoveredCharacters={discoveredCharacters}
              enrichedCharacters={enrichedCharacters}
              enrichingCharacters={enrichingCharacters}
              worldPreview={worldPreview}
              title={state.title}
            />
          )}
          
          {/* === GUIDED MODE STEPS === */}
          {step === 'foundation' && (
            <StepGuidedFoundation state={state} update={update} />
          )}
          
          {step === 'setting' && (
            <StepGuidedSetting state={state} update={update} />
          )}
          
          {step === 'characters' && (
            <StepGuidedCharacters
              state={state}
              update={update}
              onGenerateNpcStubs={runNpcStubCuration}
              isNpcGenerating={isNpcGenerating}
            />
          )}
          
          {step === 'conflict' && (
            <StepGuidedConflict state={state} update={update} />
          )}
          
          {step === 'opening' && (
            <StepGuidedOpening state={state} update={update} />
          )}
          
          {step === 'review' && (state.scaffoldResult || state.mode === 'guided') && (
            <StepReview state={state} update={update} />
          )}
        </div>
        
        {/* Footer */}
        {step !== 'companion_processing' && (
        <div className="flex justify-between px-6 py-4 border-t border-dark-600">
          <div className="flex gap-2">
            <button
              onClick={goBack}
              disabled={
                step === 'mode' ||
                step === 'processing' ||
                isLoading ||
                (lockedIntent === 'companion' && step === 'companion')
              }
              className="btn-secondary"
            >
              Back
            </button>
            {(state.title || state.prompt) && step !== 'processing' && step !== 'mode' && (
              <button
                onClick={startOver}
                disabled={isLoading}
                className="btn-secondary text-amber-400 hover:text-amber-300"
              >
                Start Over
              </button>
            )}
          </div>

          {step !== 'templates' && (
            <button
              onClick={goNext}
              disabled={isLoading || isNpcGenerating || step === 'processing' || (step === 'mode' && !state.mode)}
              className="btn-primary"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className="spinner w-4 h-4 mr-2" />
                  {step === 'processing' ? 'Scaffolding...' :
                   step === 'review' ? 'Creating...' :
                   curationProgress ? curationProgress :
                   state.mode === 'guided' ? 'Curating...' :
                   'Loading...'}
                </span>
              ) : step === 'review' ? (
                'Create World'
              ) : step === 'companion' ? (
                'Create Companion'
              ) : step === 'mode' ? (
                'Continue'
              ) : (
                'Next'
              )}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function getStepNumber(step: WizardStep, mode: CreationMode | null): number {
  if (mode === 'prompt') {
    const steps: WizardStep[] = ['mode', 'concept', 'content', 'processing', 'review'];
    return Math.max(1, steps.indexOf(step));
  } else if (mode === 'guided') {
    const steps: WizardStep[] = ['mode', 'foundation', 'setting', 'characters', 'conflict', 'opening', 'review'];
    return Math.max(1, steps.indexOf(step));
  } else if (mode === 'companion') {
    const steps: WizardStep[] = ['mode', 'companion', 'companion_processing'];
    return Math.max(1, steps.indexOf(step));
  }
  return 1;
}

function getTotalSteps(mode: CreationMode | null): number {
  if (mode === 'prompt') return 4; // mode, concept, content/processing, review
  if (mode === 'guided') return 7; // mode, foundation, setting, characters, conflict, opening, review
  if (mode === 'companion') return 3; // mode, companion, companion_processing
  return 1;
}

// ============================================
// Step Components
// ============================================

// Mode Selection Step
function StepModeSelect({ state, update, hideCompanion = false }: { state: WizardState; update: (p: Partial<WizardState>) => void; hideCompanion?: boolean }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-dark-50 mb-2">How would you like to create your world?</h3>
        <p className="text-dark-300">
          Pick a starting point — or invent your own
        </p>
      </div>

      {/* Featured: Templates (the quickest path to a playable world) */}
      <button
        onClick={() => update({ mode: 'templates' })}
        className={`w-full p-6 rounded-lg text-left transition-all border ${
          state.mode === 'templates'
            ? 'bg-primary-600/90 ring-2 ring-primary-400 border-primary-400'
            : 'bg-gradient-to-br from-primary-900/40 via-dark-700 to-dark-700 border-primary-700/40 hover:border-primary-500/60 hover:from-primary-900/60'
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="text-4xl">🎬</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-lg font-semibold text-dark-50">Start from a template</h4>
              <span className="text-[10px] uppercase tracking-wider bg-primary-500/30 text-primary-200 px-2 py-0.5 rounded-full">
                Fastest
              </span>
            </div>
            <p className={`text-sm ${state.mode === 'templates' ? 'text-primary-100' : 'text-dark-200'}`}>
              Pick from 9 hand-crafted premises — Victorian occult, generation ship, cyberpunk fixer, small-town drama. Ready to play in about a minute. You can still tweak anything.
            </p>
          </div>
        </div>
      </button>

      <div className="relative flex items-center gap-3 text-xs uppercase tracking-widest text-dark-400">
        <div className="flex-1 h-px bg-dark-500" />
        <span>or build from scratch</span>
        <div className="flex-1 h-px bg-dark-500" />
      </div>

      <div className={`grid grid-cols-1 ${hideCompanion ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        {/* Full Prompt Mode */}
        <button
          onClick={() => update({ mode: 'prompt' })}
          className={`p-6 rounded-lg text-left transition-all ${
            state.mode === 'prompt'
              ? 'bg-primary-600 ring-2 ring-primary-400'
              : 'bg-dark-700 hover:bg-dark-600'
          }`}
        >
          <div className="text-3xl mb-3">📝</div>
          <h4 className="text-lg font-semibold text-dark-50 mb-2">Full Concept</h4>
          <p className={`text-sm ${state.mode === 'prompt' ? 'text-primary-200' : 'text-dark-300'}`}>
            I have a detailed story idea ready. Paste your full world concept and let AI extract characters, settings, and structure.
          </p>
          <div className={`mt-4 text-xs ${state.mode === 'prompt' ? 'text-primary-200' : 'text-dark-400'}`}>
            Best for: Detailed prompts, existing stories, world transplants
          </div>
        </button>

        {/* Guided Build Mode */}
        <button
          onClick={() => update({ mode: 'guided' })}
          className={`p-6 rounded-lg text-left transition-all ${
            state.mode === 'guided'
              ? 'bg-primary-600 ring-2 ring-primary-400'
              : 'bg-dark-700 hover:bg-dark-600'
          }`}
        >
          <div className="text-3xl mb-3">🎯</div>
          <h4 className="text-lg font-semibold text-dark-50 mb-2">Guided Build</h4>
          <p className={`text-sm ${state.mode === 'guided' ? 'text-primary-200' : 'text-dark-300'}`}>
            Build step-by-step with AI assistance. Answer questions about genre, characters, and conflict - AI helps flesh out details.
          </p>
          <div className={`mt-4 text-xs ${state.mode === 'guided' ? 'text-primary-200' : 'text-dark-400'}`}>
            Best for: New ideas, exploring possibilities, learning the system
          </div>
        </button>

        {!hideCompanion && (
          <button
            onClick={() => update({ mode: 'companion' })}
            className={`p-6 rounded-lg text-left transition-all ${
              state.mode === 'companion'
                ? 'bg-primary-600 ring-2 ring-primary-400'
                : 'bg-dark-700 hover:bg-dark-600'
            }`}
          >
            <div className="text-3xl mb-3">🤝</div>
            <h4 className="text-lg font-semibold text-dark-50 mb-2">Companion</h4>
            <p className={`text-sm ${state.mode === 'companion' ? 'text-primary-200' : 'text-dark-300'}`}>
              Create a personal AI companion with its own voice, role, and boundaries.
            </p>
            <div className={`mt-4 text-xs ${state.mode === 'companion' ? 'text-primary-200' : 'text-dark-400'}`}>
              Best for: 1:1 conversation, memory, relationship growth
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

// Template Picker Step
function StepTemplates({ onPick }: { onPick: (template: WorldTemplate) => void }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-2">
        <h3 className="text-2xl font-bold text-dark-50 mb-2">Pick a premise</h3>
        <p className="text-dark-300">
          Each one's a starting point — you'll be able to tweak the title, tone, and details on the next step.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {WORLD_TEMPLATES.map(tpl => (
          <button
            key={tpl.id}
            onClick={() => onPick(tpl)}
            className="text-left p-5 rounded-lg bg-dark-700 hover:bg-dark-600 border border-dark-500 hover:border-primary-500/60 transition-all group"
          >
            <div className="flex items-start gap-3">
              <div className="text-3xl leading-none mt-0.5">{tpl.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h4 className="text-base font-semibold text-dark-50 group-hover:text-primary-200 transition-colors">
                    {tpl.title}
                  </h4>
                  <span className="text-[10px] uppercase tracking-wider text-dark-400 whitespace-nowrap">
                    {tpl.genreHint}
                  </span>
                </div>
                <p className="text-sm text-dark-300 leading-snug">
                  {tpl.pitch}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Guided Mode Step Components
// ============================================

function StepGuidedFoundation({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const genres = ['fantasy', 'sci_fi', 'horror', 'mystery', 'romance', 'thriller', 'historical', 'contemporary', 'post_apocalyptic', 'urban_fantasy'];
  const tones = ['epic', 'dark', 'gritty', 'whimsical', 'comedic', 'tragic', 'hopeful', 'tense', 'mysterious', 'romantic'];
  const themeOptions = ['redemption', 'power_corruption', 'love_loss', 'identity', 'survival', 'revenge', 'coming_of_age', 'good_vs_evil', 'sacrifice', 'betrayal'];
  
  const draft = state.guidedDraft as any;
  
  const updateDraft = (key: string, value: unknown) => {
    update({
      guidedDraft: {
        ...state.guidedDraft,
        [key]: value,
      } as GuidedDraft,
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Foundation</h3>
        <p className="text-dark-300 text-sm">
          Start with the basics: title, genre, and tone.
        </p>
      </div>

      <div className="rounded-md border border-primary-700/40 bg-primary-900/20 p-4 text-sm text-dark-200 space-y-2">
        <p className="font-medium text-primary-100">How to think about this</p>
        <p className="leading-relaxed">
          We'll walk through your world step by step — foundation, setting, characters,
          conflict, opening. The AI fleshes out details from your choices, and you get to
          adjust at every step.
        </p>
        <p className="leading-relaxed">
          Best if you don't have a fully-formed idea yet. Pick a genre and tone that interest
          you here; the next steps will pull a specific story out of those choices.
        </p>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">World Title *</label>
        <input
          type="text"
          value={state.title}
          onChange={(e) => update({ title: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="The Chronicles of..."
        />
      </div>
      
      {/* Genre */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Genre</label>
        <div className="flex flex-wrap gap-2">
          {genres.map(g => (
            <button
              key={g}
              onClick={() => updateDraft('selectedGenre', g)}
              className={`px-3 py-1.5 rounded text-sm capitalize ${
                draft.selectedGenre === g
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {g.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tone */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Tone</label>
        <div className="flex flex-wrap gap-2">
          {tones.map(t => (
            <button
              key={t}
              onClick={() => updateDraft('selectedTone', t)}
              className={`px-3 py-1.5 rounded text-sm capitalize ${
                draft.selectedTone === t
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      
      {/* Themes */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Themes (select 1-3)</label>
        <div className="flex flex-wrap gap-2">
          {themeOptions.map(theme => {
            const selected = draft.selectedThemes?.includes(theme);
            return (
              <button
                key={theme}
                onClick={() => {
                  const current = draft.selectedThemes || [];
                  if (selected) {
                    updateDraft('selectedThemes', current.filter((t: string) => t !== theme));
                  } else if (current.length < 3) {
                    updateDraft('selectedThemes', [...current, theme]);
                  }
                }}
                className={`px-3 py-1.5 rounded text-sm capitalize ${
                  selected
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                }`}
              >
                {theme.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Content Rating */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Content Rating</label>
        <div className="space-y-2">
          {[
            { value: 'standard', label: 'Standard', desc: 'Family-friendly content' },
            { value: 'mature', label: 'Mature', desc: 'Adult themes, moderate violence' },
            { value: 'explicit', label: 'Explicit', desc: 'Unrestricted content' },
          ].map(r => (
            <button
              key={r.value}
              onClick={() => update({ contentRating: r.value as ContentRating })}
              className={`w-full p-3 rounded text-left ${
                state.contentRating === r.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-200 hover:bg-dark-600'
              }`}
            >
              <div className="font-medium">{r.label}</div>
              <div className="text-sm opacity-70">{r.desc}</div>
            </button>
          ))}
        </div>
        <ContentRatingDescription rating={state.contentRating} />
      </div>
    </div>
  );
}

// ============================================
// Companion Mode Step
// ============================================

function StepCompanionSetup({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const roleOptions: WizardState['companionRole'][] = ['friend', 'mentor', 'coach', 'confidant', 'partner', 'custom'];
  const isMinor = isMinorApparentAge(state.companionAge);
  
  const mediumOptions: { value: WizardState['companionMedium']; label: string; description: string }[] = [
    { value: 'text_message', label: '💬 Text Messages', description: 'SMS-style texting. Short, casual messages.' },
    { value: 'chat_app', label: '📱 Chat App', description: 'Messenger/WhatsApp vibe. Casual and chatty.' },
    { value: 'social_dm', label: '🐦 Social DM', description: 'Twitter/Instagram DMs. Semi-curated.' },
    { value: 'voice_call', label: '📞 Phone Call', description: 'Voice-only. Natural spoken dialogue.' },
    { value: 'video_call', label: '📹 Video Call', description: 'FaceTime/Zoom. Can see each other.' },
    { value: 'in_person', label: '🤝 In Person', description: 'Face to face. Physical presence.' },
    { value: 'letters', label: '✉️ Letters', description: 'Written correspondence. Thoughtful and formal.' },
    { value: 'journal', label: '📓 Journal', description: 'Companion as your diary. Intimate reflection.' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Build your companion</h3>
        <p className="text-dark-300 text-sm">
          Define the companion's identity and boundaries.
        </p>
      </div>

      <div className="rounded-md border border-primary-700/40 bg-primary-900/20 p-4 text-sm text-dark-200 space-y-2">
        <p className="font-medium text-primary-100">How to think about this</p>
        <p className="leading-relaxed">
          You're describing a <span className="text-dark-50">person</span>, not a story. The
          richer your description, the more they'll feel like themselves — name, age, voice,
          what they care about, how they react under pressure.
        </p>
        <p className="leading-relaxed">
          Personality and voice matter more than plot. Anything you leave blank, the AI fills
          in plausibly; anything you specify locks in. You can edit the profile later from
          inside the chat.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">World Title *</label>
        <input
          type="text"
          value={state.title}
          onChange={(e) => update({ title: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="My Companion"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Companion Name</label>
        <input
          type="text"
          value={state.companionName}
          onChange={(e) => update({ companionName: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="Defaults to world title"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-1">Gender</label>
          <input
            type="text"
            value={state.companionGender}
            onChange={(e) => update({ companionGender: e.target.value })}
            className="narrative-input w-full px-4 py-2"
            placeholder="e.g. Female, Male, ..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-1">Pronouns</label>
          <input
            type="text"
            value={state.companionPronouns}
            onChange={(e) => update({ companionPronouns: e.target.value })}
            className="narrative-input w-full px-4 py-2"
            placeholder="e.g. she/her"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-1">Age</label>
          <input
            type="text"
            value={state.companionAge}
            onChange={(e) => update({ companionAge: e.target.value })}
            className="narrative-input w-full px-4 py-2"
            placeholder="e.g. 28, mid-30s, early 20s"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Description</label>
        <textarea
          value={state.companionDescription}
          onChange={(e) => update({ companionDescription: e.target.value })}
          className="narrative-input w-full px-4 py-2 min-h-[80px]"
          placeholder="Describe your companion — personality, appearance, backstory ideas, anything you want them to be..."
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">What should your companion call you?</label>
        <input
          type="text"
          value={state.companionUserPreferredName}
          onChange={(e) => update({ companionUserPreferredName: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="Defaults to your profile name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Role</label>
        <div className="flex flex-wrap gap-2">
          {roleOptions.map(role => (
            <button
              key={role}
              onClick={() => update({ companionRole: role })}
              className={`px-3 py-1.5 rounded text-sm capitalize ${
                state.companionRole === role
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">How are you meeting?</label>
        <p className="text-dark-400 text-xs mb-3">
          This shapes how your companion communicates - texting style vs. spoken dialogue, physical awareness, etc.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {mediumOptions.map(option => (
            <button
              key={option.value}
              onClick={() => update({ companionMedium: option.value })}
              className={`p-3 rounded text-left ${
                state.companionMedium === option.value
                  ? 'bg-primary-600/20 border border-primary-500 text-primary-200'
                  : 'bg-dark-700 border border-dark-600 text-dark-300 hover:bg-dark-600'
              }`}
            >
              <div className="text-sm font-medium">{option.label}</div>
              <div className="text-xs opacity-70 mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm text-dark-200">
          <input
            type="checkbox"
            checked={state.companionAllowRoleplay}
            onChange={(e) => update({ companionAllowRoleplay: e.target.checked })}
          />
          Allow roleplay
        </label>
        <label className="flex items-center gap-2 text-sm text-dark-200">
          <input
            type="checkbox"
            checked={!isMinor && state.companionAllowRomance}
            disabled={isMinor}
            onChange={(e) => update({ companionAllowRomance: e.target.checked })}
          />
          Allow romance
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Safety Mode</label>
        <select
          value={isMinor ? 'standard' : state.companionSafetyMode}
          disabled={isMinor}
          onChange={(e) => update({ companionSafetyMode: e.target.value as WizardState['companionSafetyMode'] })}
          className="narrative-input w-full px-4 py-2"
        >
          <option value="standard">Standard</option>
          <option value="permissive">Permissive</option>
        </select>
        {isMinor && <p className="text-xs text-amber-300 mt-2">Profiles under 18 use standard, non-romantic interaction.</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Topics to Avoid</label>
        <input
          type="text"
          value={state.companionTopicsToAvoid}
          onChange={(e) => update({ companionTopicsToAvoid: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="Comma-separated (e.g., politics, medical advice)"
        />
      </div>
    </div>
  );
}

// ============================================
// Companion Processing Step
// ============================================

function StepCompanionProcessing({
  phase,
  status,
  companionName,
  worldId,
  onRetry,
  onGoToChat,
}: {
  phase: 'creating' | 'connecting' | 'scaffolding' | 'generating' | 'complete' | 'error';
  status: string;
  companionName: string;
  worldId: string | null;
  onRetry: () => void;
  onGoToChat: () => void;
}) {
  const phases = [
    { key: 'creating', label: 'Creating world' },
    { key: 'scaffolding', label: 'Generating personality' },
    { key: 'generating', label: 'Composing first message' },
    { key: 'complete', label: 'Ready' },
  ] as const;

  const phaseOrder = ['creating', 'connecting', 'scaffolding', 'generating', 'complete'];
  const currentIdx = phaseOrder.indexOf(phase);

  // Map connecting → creating for display purposes (they're the same visual phase)
  const displayPhase = phase === 'connecting' ? 'creating' : phase;

  return (
    <div className="flex flex-col items-center justify-center min-h-[300px] space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-dark-50 mb-2">{companionName}</h3>
        <p className="text-dark-300 text-sm">Setting up your companion...</p>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-md">
        <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              phase === 'error' ? 'bg-red-500' : 'bg-primary-500'
            }`}
            style={{
              width: phase === 'error'
                ? '100%'
                : phase === 'complete'
                ? '100%'
                : `${Math.max(10, (currentIdx / (phaseOrder.length - 1)) * 100)}%`,
            }}
          />
        </div>
      </div>

      {/* Phase checklist */}
      <div className="space-y-3 w-full max-w-sm">
        {phases.map(({ key, label }) => {
          const phaseIdx = phaseOrder.indexOf(key);
          const isActive = displayPhase === key && phase !== 'error';
          const isComplete = phase !== 'error' && currentIdx > phaseIdx;
          const isPending = !isActive && !isComplete;

          return (
            <div
              key={key}
              className={`flex items-center gap-3 text-sm transition-all duration-300 ${
                isActive ? 'text-dark-50' : isComplete ? 'text-green-400' : 'text-dark-500'
              }`}
            >
              {isComplete ? (
                <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : isActive ? (
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <span className="spinner w-4 h-4" />
                </span>
              ) : (
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <span className="w-2 h-2 rounded-full bg-dark-600" />
                </span>
              )}
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      {/* Live status text */}
      {status && phase !== 'error' && (
        <p className="text-dark-400 text-xs animate-pulse">{status}</p>
      )}

      {/* Error state */}
      {phase === 'error' && (
        <div className="flex gap-3">
          <button onClick={onRetry} className="btn-primary text-sm">
            Retry
          </button>
          {worldId && (
            <button onClick={onGoToChat} className="btn-secondary text-sm">
              Go to Chat Anyway
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StepGuidedSetting({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const draft = state.guidedDraft as any;
  
  const updateDraft = (key: string, value: unknown) => {
    update({
      guidedDraft: {
        ...state.guidedDraft,
        [key]: value,
      } as GuidedDraft,
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Setting</h3>
        <p className="text-dark-300 text-sm">
          Describe where and when your story takes place.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Setting One-Liner *
        </label>
        <textarea
          value={draft.settingOneLiner || ''}
          onChange={(e) => updateDraft('settingOneLiner', e.target.value)}
          className="narrative-input w-full px-4 py-3 min-h-[100px]"
          placeholder="A dying space station orbiting a black hole, where the last survivors of humanity eke out a meager existence..."
        />
        <p className="text-dark-400 text-xs mt-1">
          AI will expand this into a rich world description
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Time Period (optional)
        </label>
        <input
          type="text"
          value={draft.timePeriod || ''}
          onChange={(e) => updateDraft('timePeriod', e.target.value)}
          className="narrative-input w-full px-4 py-2"
          placeholder="e.g., 2350 AD, Victorian era, The Forgotten Age..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Key Locations (optional, one per line)
        </label>
        <textarea
          value={draft.keyLocations?.join('\n') || ''}
          onChange={(e) => updateDraft('keyLocations', e.target.value.split('\n').filter(l => l.trim()))}
          className="narrative-input w-full px-4 py-3"
          placeholder="The Rusty Anchor Tavern
The King's Palace
The Forbidden Woods"
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          What's Impossible Here? (optional, one per line)
        </label>
        <textarea
          value={draft.whatIsImpossible?.join('\n') || ''}
          onChange={(e) => updateDraft('whatIsImpossible', e.target.value.split('\n').filter(l => l.trim()))}
          className="narrative-input w-full px-4 py-3"
          placeholder="Magic doesn't exist
Faster-than-light travel
Resurrection"
          rows={3}
        />
        <p className="text-dark-400 text-xs mt-1">
          These become hard rules in your world
        </p>
      </div>
    </div>
  );
}

function StepGuidedCharacters({
  state,
  update,
  onGenerateNpcStubs,
  isNpcGenerating,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onGenerateNpcStubs: () => void;
  isNpcGenerating: boolean;
}) {
  const [playerDraft, setPlayerDraft] = useState({
    name: '',
    roleArchetype: '',
    oneLinerConcept: '',
  });
  const [npcDraft, setNpcDraft] = useState({
    name: '',
    roleArchetype: '',
    oneLinerConcept: '',
  });

  const playerStub = state.guidedDraft.characterStubs.find(stub => stub.isPlayerCharacter);
  const npcStubs = state.guidedDraft.characterStubs.filter(stub => !stub.isPlayerCharacter);

  const addPlayerCharacter = () => {
    if (playerStub) return;
    if (!playerDraft.name.trim() || !playerDraft.oneLinerConcept.trim()) return;

    update({
      guidedDraft: {
        ...state.guidedDraft,
        characterStubs: [
          ...state.guidedDraft.characterStubs,
          {
            name: playerDraft.name.trim(),
            roleArchetype: playerDraft.roleArchetype.trim(),
            oneLinerConcept: playerDraft.oneLinerConcept.trim(),
            isPlayerCharacter: true,
          },
        ],
      },
    });

    setPlayerDraft({ name: '', roleArchetype: '', oneLinerConcept: '' });
  };

  const addNpcCharacter = () => {
    if (!playerStub) return;
    if (!npcDraft.name.trim() || !npcDraft.oneLinerConcept.trim()) return;

    update({
      guidedDraft: {
        ...state.guidedDraft,
        characterStubs: [
          ...state.guidedDraft.characterStubs,
          {
            name: npcDraft.name.trim(),
            roleArchetype: npcDraft.roleArchetype.trim(),
            oneLinerConcept: npcDraft.oneLinerConcept.trim(),
            isPlayerCharacter: false,
          },
        ],
      },
    });

    setNpcDraft({ name: '', roleArchetype: '', oneLinerConcept: '' });
  };

  const removeNpcCharacter = (npcIndex: number) => {
    const npcIndices = state.guidedDraft.characterStubs.reduce<number[]>((acc, stub, idx) => {
      if (!stub.isPlayerCharacter) acc.push(idx);
      return acc;
    }, []);
    const targetIndex = npcIndices[npcIndex];
    if (targetIndex === undefined) return;

    update({
      guidedDraft: {
        ...state.guidedDraft,
        characterStubs: state.guidedDraft.characterStubs.filter((_, i) => i !== targetIndex),
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Characters</h3>
        <p className="text-dark-300 text-sm">
          Add your player character first, then generate 3-5 additional NPCs with AI.
        </p>
      </div>

      {/* Player Character */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-dark-200">Player Character (required)</label>
        {playerStub ? (
          <div className="flex items-center gap-2 bg-dark-700 p-3 rounded">
            <div className="flex-1">
              <div className="text-dark-50 font-medium">
                {playerStub.name}
                <span className="ml-2 text-xs bg-primary-600 px-2 py-0.5 rounded">Player</span>
              </div>
              <div className="text-dark-400 text-sm">
                {playerStub.roleArchetype && `${playerStub.roleArchetype} - `}{playerStub.oneLinerConcept}
              </div>
            </div>
            <span className="text-xs text-dark-400">Locked</span>
          </div>
        ) : (
          <div className="bg-dark-700/50 rounded p-4 space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={playerDraft.name}
                  onChange={(e) => setPlayerDraft(prev => ({ ...prev, name: e.target.value }))}
                  className="narrative-input w-full px-3 py-2 text-sm"
                  placeholder="Player name *"
                />
              </div>
              <div className="w-40">
                <input
                  type="text"
                  value={playerDraft.roleArchetype}
                  onChange={(e) => setPlayerDraft(prev => ({ ...prev, roleArchetype: e.target.value }))}
                  className="narrative-input w-full px-3 py-2 text-sm"
                  placeholder="Role (e.g., Rebel)"
                />
              </div>
            </div>
            <textarea
              value={playerDraft.oneLinerConcept}
              onChange={(e) => setPlayerDraft(prev => ({ ...prev, oneLinerConcept: e.target.value }))}
              className="narrative-input w-full px-3 py-2 text-sm"
              placeholder="One-line player concept *"
              rows={2}
            />
            <div className="flex items-center justify-between">
              <p className="text-dark-400 text-xs">Player character is locked after creation.</p>
              <button
                onClick={addPlayerCharacter}
                disabled={!playerDraft.name.trim() || !playerDraft.oneLinerConcept.trim()}
                className="btn-primary text-sm py-1.5 px-4"
              >
                Add Player Character
              </button>
            </div>
          </div>
        )}
      </div>

      {/* NPCs */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-dark-200">Additional Characters (NPCs)</label>
          <button
            onClick={onGenerateNpcStubs}
            disabled={!playerStub || isNpcGenerating}
            className="btn-secondary text-sm py-1.5 px-4"
            title={!playerStub ? 'Add a player character first' : 'Generate 3-5 NPCs'}
          >
            {isNpcGenerating ? 'Generating...' : 'Enhance with AI (add 3-5 NPCs)'}
          </button>
        </div>

        {npcStubs.length > 0 ? (
          <div className="space-y-2">
            {npcStubs.map((char, i) => (
              <div key={`${char.name}-${i}`} className="flex items-center gap-2 bg-dark-700 p-3 rounded">
                <div className="flex-1">
                  <div className="text-dark-50 font-medium">{char.name}</div>
                  <div className="text-dark-400 text-sm">
                    {char.roleArchetype && `${char.roleArchetype} - `}{char.oneLinerConcept}
                  </div>
                </div>
                <button
                  onClick={() => removeNpcCharacter(i)}
                  className="text-dark-400 hover:text-red-400"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-dark-400 text-sm">No NPCs yet. Add manually or use AI to generate 3-5.</p>
        )}

        <div className="bg-dark-700/50 rounded p-4 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={npcDraft.name}
                onChange={(e) => setNpcDraft(prev => ({ ...prev, name: e.target.value }))}
                className="narrative-input w-full px-3 py-2 text-sm"
                placeholder="NPC name *"
                disabled={!playerStub}
              />
            </div>
            <div className="w-40">
              <input
                type="text"
                value={npcDraft.roleArchetype}
                onChange={(e) => setNpcDraft(prev => ({ ...prev, roleArchetype: e.target.value }))}
                className="narrative-input w-full px-3 py-2 text-sm"
                placeholder="Role (e.g., Rival)"
                disabled={!playerStub}
              />
            </div>
          </div>
          <textarea
            value={npcDraft.oneLinerConcept}
            onChange={(e) => setNpcDraft(prev => ({ ...prev, oneLinerConcept: e.target.value }))}
            className="narrative-input w-full px-3 py-2 text-sm"
            placeholder="One-line NPC concept *"
            rows={2}
            disabled={!playerStub}
          />
          <div className="flex items-center justify-between">
            <p className="text-dark-400 text-xs">Manual NPCs are appended; AI never replaces.</p>
            <button
              onClick={addNpcCharacter}
              disabled={!playerStub || !npcDraft.name.trim() || !npcDraft.oneLinerConcept.trim()}
              className="btn-primary text-sm py-1.5 px-4"
            >
              Add NPC
            </button>
          </div>
        </div>
      </div>

      <p className="text-dark-400 text-xs">
        💡 Tip: The AI uses everything you've provided so far, including this step, to add 3-5 NPCs.
      </p>
    </div>
  );
}

function StepGuidedConflict({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const draft = state.guidedDraft as any;
  
  const updateDraft = (key: string, value: unknown) => {
    update({
      guidedDraft: {
        ...state.guidedDraft,
        [key]: value,
      } as GuidedDraft,
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Central Conflict</h3>
        <p className="text-dark-300 text-sm">
          What's the dramatic question driving your story?
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Conflict Idea *
        </label>
        <textarea
          value={draft.conflictIdea || ''}
          onChange={(e) => updateDraft('conflictIdea', e.target.value)}
          className="narrative-input w-full px-4 py-3 min-h-[100px]"
          placeholder="The protagonist must choose between saving their family and saving the city..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Stakes (optional)
        </label>
        <textarea
          value={draft.stakes || ''}
          onChange={(e) => updateDraft('stakes', e.target.value)}
          className="narrative-input w-full px-4 py-3"
          placeholder="If they fail, the ancient evil will consume the world..."
          rows={2}
        />
      </div>
      
      <p className="text-dark-400 text-xs">
        💡 Tip: Good conflicts have no easy solutions and connect to character psychology.
      </p>
    </div>
  );
}

function StepGuidedOpening({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const draft = state.guidedDraft as any;
  const locations = draft.setting?.keyLocations || [];
  
  const updateDraft = (key: string, value: unknown) => {
    update({
      guidedDraft: {
        ...state.guidedDraft,
        [key]: value,
      } as GuidedDraft,
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Opening Scene</h3>
        <p className="text-dark-300 text-sm">
          How does your story begin? AI will craft multiple opening hooks.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">
          Opening Idea (optional)
        </label>
        <textarea
          value={draft.openingIdea || ''}
          onChange={(e) => updateDraft('openingIdea', e.target.value)}
          className="narrative-input w-full px-4 py-3"
          placeholder="The story opens with the protagonist receiving a mysterious letter..."
          rows={3}
        />
        <p className="text-dark-400 text-xs mt-1">
          Leave blank to let AI suggest openings based on your conflict
        </p>
      </div>
      
      {locations.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-2">
            Starting Location
          </label>
          <div className="flex flex-wrap gap-2">
            {locations.map((loc: any) => (
              <button
                key={loc.name || loc}
                onClick={() => updateDraft('startingLocation', loc.name || loc)}
                className={`px-3 py-1.5 rounded text-sm ${
                  draft.startingLocation === (loc.name || loc)
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                }`}
              >
                {loc.name || loc}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Opening Mood</label>
        <div className="flex flex-wrap gap-2">
          {['tense', 'mysterious', 'action', 'contemplative', 'chaotic', 'peaceful'].map(mood => (
            <button
              key={mood}
              onClick={() => updateDraft('openingMood', mood)}
              className={`px-3 py-1.5 rounded text-sm capitalize ${
                draft.openingMood === mood
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {mood}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Prompt Mode Step Components
// ============================================

function StepConcept({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  // Real-time token count for the prompt
  const promptTokens = useMemo(() => {
    if (!state.prompt) return 0;
    return encode(state.prompt).length;
  }, [state.prompt]);
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">What's your story about?</h3>
        <p className="text-dark-300 text-sm">
          Describe your world, characters, and the situation you want to explore. Be as detailed as you like - our AI will extract characters, facts, and structure from your description.
        </p>
      </div>

      <div className="rounded-md border border-primary-700/40 bg-primary-900/20 p-4 text-sm text-dark-200 space-y-2">
        <p className="font-medium text-primary-100">How to think about this</p>
        <p className="leading-relaxed">
          You're writing a story <span className="text-dark-50">premise</span>, not a chat
          opener. A paragraph or two works best: the setting, who you play, what's at stake,
          what the opening scene looks like.
        </p>
        <p className="leading-relaxed">
          Concrete beats vague — names, places, and a hook will give you a sharper world than
          "a fantasy adventure." Don't worry about format; just tell us what the world is.
        </p>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-dark-200 mb-1">
          World Title *
        </label>
        <input
          type="text"
          id="title"
          value={state.title}
          onChange={(e) => update({ title: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="The Chronicles of..."
        />
      </div>
      
      <div>
        <label htmlFor="prompt" className="block text-sm font-medium text-dark-200 mb-1">
          World Concept *
        </label>
        <textarea
          id="prompt"
          value={state.prompt}
          onChange={(e) => update({ prompt: e.target.value })}
          className="narrative-input w-full px-4 py-3 min-h-[200px] resize-y"
          placeholder={`Describe your world in detail. Include:

• The setting and world (time period, location, rules of the world)
• Main characters with their descriptions, personalities, backgrounds
• The initial situation or conflict
• Any specific themes or elements you want to explore

Example: "A cyberpunk noir story set in Neo-Tokyo, 2087. The protagonist is Maya Chen, a 28-year-old former corporate hacker with cybernetic eyes and a troubled past..."`}
        />
        <div className="flex justify-between items-center mt-1">
          <p className="text-dark-400 text-xs">
            💡 Tip: The more detail you provide about characters, the better we can capture their essence.
          </p>
          <p className="text-dark-500 text-xs tabular-nums">
            {promptTokens.toLocaleString()} tokens
          </p>
        </div>
      </div>
    </div>
  );
}

function StepContent({ 
  state, 
  update,
  availableModels,
  modelsLoading,
  isAdmin,
}: { 
  state: WizardState; 
  update: (p: Partial<WizardState>) => void;
  availableModels: ModelInfo[];
  modelsLoading: boolean;
  isAdmin: boolean;
}) {
  const toggleRule = (rule: string) => {
    const rules = state.worldRules.includes(rule)
      ? state.worldRules.filter(r => r !== rule)
      : [...state.worldRules, rule];
    update({ worldRules: rules });
  };
  
  // Get unique providers from available models
  const providers = [...new Set(availableModels.map(m => m.provider))];
  
  // Get models for selected provider
  const modelsForProvider = availableModels.filter(m => m.provider === state.provider);
  
  // Get selected model info
  const selectedModel = availableModels.find(m => m.provider === state.provider && m.modelId === state.model);

  const reasoningEffortOptions = REASONING_EFFORT_OPTIONS.filter(option =>
    !selectedModel?.supportedReasoningEfforts?.length ||
    selectedModel.supportedReasoningEfforts.includes(option.value)
  );
  
  // Handle provider change
  const handleProviderChange = (newProvider: string) => {
    // Auto-select first model for this provider
    const firstModel = availableModels.find(m => m.provider === newProvider);
    update({
      provider: newProvider,
      ...(firstModel ? { model: firstModel.modelId } : {}),
      reasoningEffort: null,
    });
  };

  const handleModelChange = (newModel: string) => {
    const nextModel = availableModels.find(m => m.provider === state.provider && m.modelId === newModel);
    const effortStillSupported = !state.reasoningEffort ||
      !nextModel?.supportedReasoningEfforts?.length ||
      nextModel.supportedReasoningEfforts.includes(state.reasoningEffort);
    update({
      model: newModel,
      ...(!effortStillSupported ? { reasoningEffort: null } : {}),
    });
  };
  
  const formatCost = (input: number, output: number) => {
    return `$${input.toFixed(2)} / $${output.toFixed(2)} per 1M tokens`;
  };
  
  const formatContext = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`;
    return tokens.toString();
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Settings & Configuration</h3>
        <p className="text-dark-300 text-sm">
          {isAdmin
            ? 'Configure content boundaries, AI model, and world rules.'
            : 'Configure content boundaries and world rules.'}
        </p>
      </div>
      
      {/* Content Rating */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Content Rating</label>
        <div className="space-y-2">
          {[
            { value: 'standard', label: 'Standard', desc: 'Family-friendly. Violence implied, romance limited to tension.' },
            { value: 'mature', label: 'Mature', desc: 'Adult themes, moderate violence, fade-to-black intimacy.' },
            { value: 'explicit', label: 'Explicit', desc: 'Unrestricted content including graphic scenes.' },
          ].map(r => (
            <button
              key={r.value}
              onClick={() => update({ contentRating: r.value as ContentRating })}
              className={`w-full p-3 rounded text-left ${
                state.contentRating === r.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-200 hover:bg-dark-600'
              }`}
            >
              <div className="font-medium">{r.label}</div>
              <div className="text-sm opacity-70">{r.desc}</div>
            </button>
          ))}
        </div>
        <ContentRatingDescription rating={state.contentRating} />
      </div>
      
      {/* NSFW Fallback */}
      {state.contentRating === 'explicit' && (
        <div className="bg-amber-900/20 border border-amber-700 rounded p-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={state.allowNsfw}
              onChange={(e) => update({ allowNsfw: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-amber-200 text-sm">
              Allow fallback to permissive models if primary model refuses content
            </span>
          </label>
        </div>
      )}
      
      {/* World Rules */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-dark-200">World Rules</label>
          <button
            onClick={() => update({ worldRules: [...DEFAULT_RULES] })}
            className="text-xs text-primary-400 hover:text-primary-300"
          >
            Select All
          </button>
        </div>
        <div className="space-y-2">
          {DEFAULT_RULES.map(rule => (
            <button
              key={rule}
              onClick={() => toggleRule(rule)}
              className={`w-full p-2 rounded text-left text-sm ${
                state.worldRules.includes(rule)
                  ? 'bg-dark-600 text-dark-50'
                  : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
              }`}
            >
              <span className="mr-2">{state.worldRules.includes(rule) ? '✓' : '○'}</span>
              {rule}
            </button>
          ))}
        </div>
        
        <textarea
          value={state.customRules}
          onChange={(e) => update({ customRules: e.target.value })}
          className="narrative-input w-full px-3 py-2 mt-3 text-sm"
          placeholder="Add custom rules (one per line)..."
          rows={2}
        />
      </div>
      
      {/* AI Model Configuration */}
      {isAdmin && (
        <div className="border-t border-dark-600 pt-6">
          <h4 className="text-sm font-medium text-dark-50 mb-4 flex items-center gap-2">
            <span>🤖</span> AI Model Configuration
          </h4>
          
          {modelsLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="spinner w-6 h-6" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Provider dropdown */}
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">
                  Provider
                </label>
                <select
                  value={state.provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                >
                  {providers.map(p => (
                    <option key={p} value={p}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Model dropdown */}
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">
                  Model
                </label>
                <select
                  value={state.model}
                  onChange={(e) => handleModelChange(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                >
                  {modelsForProvider.map(m => (
                    <option key={m.id} value={m.modelId}>
                      {m.displayName}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Model Info Card */}
              {selectedModel && (
                <div className="bg-dark-700/50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedModel.categories.map(cat => (
                      <span
                        key={cat}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          cat === 'reasoning' ? 'bg-purple-600/30 text-purple-300' :
                          cat === 'fast' ? 'bg-green-600/30 text-green-300' :
                          cat === 'permissive' ? 'bg-orange-600/30 text-orange-300' :
                          'bg-blue-600/30 text-blue-300'
                        }`}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-dark-300">
                    <div>
                      <span className="text-dark-400">Context:</span>{' '}
                      <span className="text-dark-50">{formatContext(selectedModel.contextWindow)} tokens</span>
                    </div>
                    <div>
                      <span className="text-dark-400">Latency:</span>{' '}
                      <span className="text-dark-50">~{selectedModel.avgLatencyMs}ms</span>
                    </div>
                  </div>
                  <div className="text-dark-400">
                    <span>Cost:</span>{' '}
                    <span className="text-dark-200">{formatCost(selectedModel.pricing.input, selectedModel.pricing.output)}</span>
                  </div>
                  {selectedModel.supportsReasoning && (
                    <div className="text-purple-300 text-xs">
                      ✨ Extended thinking {selectedModel.reasoningMandatory ? 'always on' : 'enabled'}
                    </div>
                  )}
                </div>
              )}
              
              {/* Reasoning Effort Control */}
              {selectedModel?.supportsReasoning && (
                <div>
                  <label className="block text-sm font-medium text-dark-200 mb-1">
                    Thinking Depth
                  </label>
                  <select
                    value={state.reasoningEffort || ''}
                    onChange={(e) => update({ reasoningEffort: e.target.value ? e.target.value as WizardState['reasoningEffort'] : null })}
                    className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                  >
                    <option value="">
                      Auto{selectedModel.defaultReasoningEffort
                        ? ` (${selectedModel.defaultReasoningEffort} default${selectedModel.reasoningMandatory ? ', always on' : ''})`
                        : ' (model decides)'}
                    </option>
                    {reasoningEffortOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <p className="text-dark-400 text-xs mt-1">
                    Higher levels use more thinking tokens and may increase cost/latency
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
    </div>
  );
}

function StepProcessing({ progress }: { progress: string[] }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="relative mb-6">
        <div className="spinner w-16 h-16" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-primary-400 text-lg font-bold">{progress.length}</span>
        </div>
      </div>
      <h3 className="text-lg font-medium text-dark-50 mb-2">Building Your World</h3>
      <p className="text-dark-300 text-sm text-center max-w-md mb-6">
        The AI is analyzing your concept, creating characters, and establishing the rules of your reality...
      </p>
      
      {/* Progress list with animations */}
      <div className="w-full max-w-md space-y-2 max-h-64 overflow-y-auto">
        {progress.map((msg, i) => {
          // Check if it's a character creation message
          const isCharacter = msg.toLowerCase().includes('character:');
          const isFact = msg.toLowerCase().includes('fact about');
          const isComplete = msg.toLowerCase().includes('complete');
          const isError = msg.toLowerCase().startsWith('error');
          
          return (
            <div 
              key={i} 
              className={`text-sm flex items-center gap-2 animate-fadeIn ${
                isComplete ? 'text-green-400' : 
                isError ? 'text-red-400' :
                isCharacter ? 'text-purple-400' :
                isFact ? 'text-blue-400' :
                'text-dark-300'
              }`}
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <span className={isComplete ? 'text-green-400' : isError ? 'text-red-400' : 'text-primary-400'}>
                {isComplete ? '✓' : isError ? '✗' : '→'}
              </span>
              {msg}
            </div>
          );
        })}
        
        {/* Show waiting indicator if no recent progress */}
        {progress.length > 0 && !progress[progress.length - 1]?.toLowerCase().includes('complete') && (
          <div className="text-sm text-dark-400 flex items-center gap-2 animate-pulse">
            <span className="text-dark-400">⋯</span>
            Thinking...
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// NEW: Rich Progress Display (V2)
// ============================================

interface StepProcessingV2Props {
  progressEvents: ScaffoldProgressEvent[];
  currentProgress: number;
  discoveredCharacters: string[];
  enrichedCharacters: Set<string>;
  enrichingCharacters: Set<string>;
  worldPreview: {
    genre?: string;
    tone?: string;
    themes?: string[];
    settingPreview?: string;
  };
  title: string;
}

function StepProcessingV2({ 
  progressEvents, 
  currentProgress, 
  discoveredCharacters,
  enrichedCharacters,
  enrichingCharacters,
  worldPreview,
  title 
}: StepProcessingV2Props) {
  // Determine current phase for display
  const lastEvent = progressEvents[progressEvents.length - 1];
  const phase = lastEvent?.phase || 'starting';
  
  // Phase-specific styling
  const getPhaseColor = (p: ScaffoldPhase) => {
    if (p.startsWith('story')) return 'text-blue-400';
    if (p.startsWith('characters')) return 'text-purple-400';
    if (p.startsWith('enriching') || p.startsWith('enrichment') || p.startsWith('phase2')) return 'text-amber-400';
    if (p === 'complete') return 'text-green-400';
    if (p === 'error') return 'text-red-400';
    return 'text-primary-400';
  };
  
  const getPhaseIcon = (p: ScaffoldPhase) => {
    if (p.startsWith('story')) return '🌍';
    if (p.startsWith('characters')) return '👥';
    if (p.startsWith('enriching') || p.startsWith('enrichment') || p.startsWith('phase2')) return '🎭';
    if (p === 'complete') return '🎉';
    if (p === 'error') return '❌';
    return '⚡';
  };
  
  return (
    <div className="flex flex-col py-6">
      {/* Title & Progress Bar */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-dark-50 mb-2">
          Creating: {title}
        </h3>
        <div className="w-full max-w-md mx-auto bg-dark-700 rounded-full h-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary-500 via-purple-500 to-amber-500 transition-all duration-500 ease-out"
            style={{ width: `${currentProgress}%` }}
          />
        </div>
        <p className="text-dark-300 text-sm mt-2">{currentProgress}% complete</p>
      </div>
      
      {/* Two-column layout: World Preview + Characters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Left: World Preview */}
        <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-600">
          <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
            <span>🌍</span> World Taking Shape
          </h4>
          
          {worldPreview.genre || worldPreview.tone ? (
            <div className="space-y-2">
              {worldPreview.genre && (
                <div className="flex items-center gap-2">
                  <span className="text-dark-400 text-xs w-16">Genre:</span>
                  <span className="text-dark-50 text-sm capitalize animate-fadeIn">{worldPreview.genre}</span>
                </div>
              )}
              {worldPreview.tone && (
                <div className="flex items-center gap-2">
                  <span className="text-dark-400 text-xs w-16">Tone:</span>
                  <span className="text-dark-50 text-sm capitalize animate-fadeIn">{worldPreview.tone}</span>
                </div>
              )}
              {worldPreview.themes && worldPreview.themes.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-dark-400 text-xs w-16">Themes:</span>
                  <div className="flex flex-wrap gap-1">
                    {worldPreview.themes.map((theme, i) => (
                      <span 
                        key={i} 
                        className="text-xs bg-dark-600 text-dark-200 px-2 py-0.5 rounded animate-fadeIn"
                        style={{ animationDelay: `${i * 100}ms` }}
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {worldPreview.settingPreview && (
                <div className="mt-3 pt-3 border-t border-dark-600">
                  <p className="text-dark-300 text-xs italic animate-fadeIn">
                    "{worldPreview.settingPreview}"
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-20">
              <div className="animate-pulse text-dark-400 text-sm">Analyzing world concept...</div>
            </div>
          )}
        </div>
        
        {/* Right: Characters Discovered */}
        <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-600">
          <h4 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
            <span>👥</span> Characters ({discoveredCharacters.length})
            {enrichedCharacters.size > 0 && enrichedCharacters.size < discoveredCharacters.length && (
              <span className="text-xs text-amber-400">
                ({enrichedCharacters.size}/{discoveredCharacters.length} enriched)
              </span>
            )}
          </h4>
          
          {discoveredCharacters.length > 0 ? (
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {discoveredCharacters.map((name, i) => {
                const isEnriched = enrichedCharacters.has(name);
                const isEnriching = enrichingCharacters.has(name);
                
                return (
                  <div 
                    key={name} 
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm animate-fadeIn
                      transition-all duration-500 ease-out
                      ${isEnriched 
                        ? 'bg-amber-900/40 border border-amber-600/60 text-amber-200' 
                        : isEnriching
                          ? 'bg-gradient-to-r from-purple-900/40 via-amber-900/30 to-amber-900/40 border border-amber-500/50 text-amber-100 animate-gradient-shift'
                          : 'bg-purple-900/30 border border-purple-700/50 text-purple-200'
                      }
                    `}
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <span className={`
                      w-2 h-2 rounded-full transition-colors duration-300
                      ${isEnriched 
                        ? 'bg-amber-400' 
                        : isEnriching
                          ? 'bg-amber-400 animate-pulse'
                          : 'bg-purple-400 animate-pulse'
                      }
                    `} />
                    {name}
                    {isEnriching && (
                      <span className="ml-1 text-xs opacity-70">✨</span>
                    )}
                    {isEnriched && (
                      <span className="ml-1 text-xs opacity-70">✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-20">
              <div className="animate-pulse text-dark-400 text-sm">Scanning for characters...</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Live Activity Feed */}
      <div className="bg-dark-800/30 rounded-lg p-4 border border-dark-700">
        <h4 className="text-sm font-medium text-dark-300 mb-3">Live Activity</h4>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {progressEvents.slice(-15).map((event, i) => (
            <div 
              key={i} 
              className={`text-sm flex items-center gap-2 animate-fadeIn ${getPhaseColor(event.phase)}`}
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <span className="w-5 text-center">{getPhaseIcon(event.phase)}</span>
              <span className="flex-1">{event.message}</span>
              <span className="text-dark-500 text-xs">{event.progress}%</span>
            </div>
          ))}
          
          {/* Animated thinking indicator */}
          {phase !== 'complete' && phase !== 'error' && (
            <div className="text-sm flex items-center gap-2 text-dark-400 animate-pulse">
              <span className="w-5 text-center">⋯</span>
              <span>Processing...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Phase indicator pills */}
      <div className="flex justify-center gap-4 mt-6">
        <PhasePill 
          label="World" 
          icon="🌍" 
          active={phase.startsWith('story') || phase === 'phase1_parallel' || phase === 'starting'}
          complete={phase.startsWith('characters') || phase.startsWith('phase2') || phase.startsWith('enriching') || phase === 'complete'}
        />
        <PhasePill 
          label="Characters" 
          icon="👥" 
          active={phase.startsWith('characters')}
          complete={phase.startsWith('phase2') || phase.startsWith('enriching') || phase === 'enrichment_complete' || phase === 'complete'}
        />
        <PhasePill 
          label="Details" 
          icon="🎭" 
          active={phase.startsWith('phase2') || phase.startsWith('enriching')}
          complete={phase === 'enrichment_complete' || phase === 'finalizing' || phase === 'complete'}
        />
        <PhasePill 
          label="Done" 
          icon="✨" 
          active={phase === 'finalizing' || phase === 'complete'}
          complete={phase === 'complete'}
        />
      </div>
    </div>
  );
}

function PhasePill({ label, icon, active, complete }: { 
  label: string; 
  icon: string; 
  active: boolean; 
  complete: boolean;
}) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all duration-300 ${
      complete ? 'bg-green-900/40 text-green-400 border border-green-700/50' :
      active ? 'bg-primary-900/40 text-primary-400 border border-primary-700/50 animate-pulse' :
      'bg-dark-700 text-dark-400 border border-dark-600'
    }`}>
      <span>{complete ? '✓' : icon}</span>
      <span>{label}</span>
    </div>
  );
}

function StepReview({ state, update }: {
  state: WizardState;
  update: (p: Partial<WizardState> | ((prev: WizardState) => Partial<WizardState>)) => void;
}) {
  const [isReextracting, setIsReextracting] = useState(false);
  const [reextractError, setReextractError] = useState('');
  const [editingCharacter, setEditingCharacter] = useState<ScaffoldedCharacter | null>(null);
  
  // Build a unified review result from either scaffoldResult (prompt mode) or guidedDraft (guided mode)
  const result = useMemo(() => {
    // Prompt mode: use scaffoldResult directly
    if (state.scaffoldResult) {
      return state.scaffoldResult;
    }
    
    // Guided mode: construct result from guidedDraft
    if (state.mode === 'guided' && state.guidedDraft) {
      const draft = state.guidedDraft;
      return {
        genre: draft.foundation?.genre || 'fantasy',
        tone: draft.foundation?.tone || 'epic',
        themes: draft.foundation?.themes || [],
        settingDefinition: draft.setting?.settingDefinition || '',
        worldRules: draft.setting?.worldRules || [],
        characters: draft.characters || [],
        relationships: draft.relationships || [],
        initialSituation: draft.opening?.initialSituation || '',
        initialFacts: [],
      } as WorldScaffoldingResult;
    }
    
    return null;
  }, [state.scaffoldResult, state.mode, state.guidedDraft]);
  
  // Guard against null result
  if (!result) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-dark-400">Loading review...</div>
      </div>
    );
  }
  
  // Check if any character was auto-marked as player
  const hasAutoPlayer = result.characters.some(c => c.isPlayerCharacter);
  
  // Determine current player selection (either user-selected or auto-detected)
  const currentPlayerName = state.selectedPlayerCharacter || 
    result.characters.find(c => c.isPlayerCharacter)?.name || 
    null;
  
  // Helper to update the correct source based on mode
  const updateResult = (changes: Partial<WorldScaffoldingResult>) => {
    if (state.scaffoldResult) {
      // Prompt mode: update scaffoldResult
      update({
        scaffoldResult: {
          ...state.scaffoldResult,
          ...changes,
        }
      });
    } else if (state.mode === 'guided' && state.guidedDraft) {
      // Guided mode: update guidedDraft fields
      const updates: Partial<GuidedDraft> = {};
      if (changes.characters !== undefined) {
        updates.characters = changes.characters;
      }
      if (changes.initialSituation !== undefined) {
        updates.opening = {
          ...state.guidedDraft.opening,
          initialSituation: changes.initialSituation,
          startingLocation: state.guidedDraft.opening?.startingLocation || '',
        };
      }
      if (changes.settingDefinition !== undefined) {
        updates.setting = {
          ...state.guidedDraft.setting,
          settingDefinition: changes.settingDefinition,
          worldRules: state.guidedDraft.setting?.worldRules || [],
          keyLocations: state.guidedDraft.setting?.keyLocations || [],
          timePeriod: state.guidedDraft.setting?.timePeriod || '',
        };
      }
      update({
        guidedDraft: {
          ...state.guidedDraft,
          ...updates,
        }
      });
    }
  };
  
  // Handle character update from edit modal
  const handleCharacterSave = (updatedCharacter: ScaffoldedCharacter) => {
    const updatedCharacters = result.characters.map(c => 
      c.name === editingCharacter?.name ? updatedCharacter : c
    );
    updateResult({ characters: updatedCharacters });
    // Update player selection if name changed
    if (editingCharacter?.name === currentPlayerName && updatedCharacter.name !== editingCharacter.name) {
      update({ selectedPlayerCharacter: updatedCharacter.name });
    }
    setEditingCharacter(null);
  };
  
  // Find characters missing from the cast; the backend dedupes and enriches them
  const handleReextract = async () => {
    setIsReextracting(true);
    setReextractError('');
    
    try {
      const response = await reextractCharacters({
        // Guided worlds have no free-text concept; the backend extracts from the setting
        prompt: state.mode === 'prompt' ? state.prompt : undefined,
        settingDefinition: result.settingDefinition,
        contentRating: state.contentRating,
        existingCharacterNames: result.characters.map(c => c.name),
        genre: result.genre,
        tone: result.tone,
        themes: result.themes,
      });
      
      // Append to the latest cast: the user may have edited characters while this ran
      const found = response.characters;
      if (found.length > 0) {
        update(prev => {
          if (prev.scaffoldResult) {
            return { scaffoldResult: { ...prev.scaffoldResult, characters: [...prev.scaffoldResult.characters, ...found] } };
          }
          if (prev.mode === 'guided' && prev.guidedDraft) {
            return { guidedDraft: { ...prev.guidedDraft, characters: [...prev.guidedDraft.characters, ...found] } };
          }
          return {};
        });
      }
      
      if (response.failedNames.length > 0) {
        setReextractError(`Found ${response.failedNames.join(', ')} but couldn't build their profiles. Try Find Missing again.`);
      } else if (response.characters.length === 0) {
        setReextractError('No additional characters found.');
      }
      
    } catch (err) {
      setReextractError(err instanceof Error ? err.message : 'Re-extraction failed');
    } finally {
      setIsReextracting(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-2">Review Your World</h3>
        <p className="text-dark-300 text-sm">
          Here's what we extracted from your concept. Review and adjust as needed.
        </p>
      </div>
      
      {/* Setting */}
      <div className="bg-dark-700 rounded p-4">
        <h4 className="text-sm font-medium text-dark-300 mb-2">Setting</h4>
        <p className="text-dark-50 text-sm whitespace-pre-wrap">{result.settingDefinition}</p>
      </div>
      
      {/* Genre/Tone/Themes */}
      <div className="flex gap-4">
        <div className="bg-dark-700 rounded p-3 flex-1">
          <div className="text-xs text-dark-400">Genre</div>
          <div className="text-dark-50 font-medium capitalize">{result.genre.replace(/_/g, ' ')}</div>
        </div>
        <div className="bg-dark-700 rounded p-3 flex-1">
          <div className="text-xs text-dark-400">Tone</div>
          <div className="text-dark-50 font-medium capitalize">{result.tone}</div>
        </div>
      </div>
      
      {result.themes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {result.themes.map(theme => (
            <span key={theme} className="px-2 py-1 bg-primary-600/30 text-primary-300 rounded text-sm">
              {theme}
            </span>
          ))}
        </div>
      )}
      
      {/* Unified Character Selection & List */}
      <div className="bg-dark-700 rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-dark-50 flex items-center gap-2">
            <span className="text-primary-400">🎮</span>
            Characters ({result.characters.length})
          </h4>
          <button
            onClick={handleReextract}
            disabled={isReextracting}
            className="text-xs px-3 py-1.5 bg-primary-600/20 hover:bg-primary-600/40 text-primary-300 rounded transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isReextracting ? (
              <>
                <div className="w-3 h-3 border border-primary-300 border-t-transparent rounded-full animate-spin" />
                Finding...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Find Missing
              </>
            )}
          </button>
        </div>
        
        {reextractError && (
          <div className="mb-3 p-2 bg-amber-600/20 border border-amber-600/30 rounded text-amber-200 text-xs">
            {reextractError}
          </div>
        )}
        
        <p className="text-dark-400 text-xs mb-3">
          Choose who you'll play by selecting the circle on the left. Click a card to edit that character.
        </p>

        {result.characters.length > 0 ? (
          <div className="space-y-2" role="radiogroup" aria-label="Choose your player character">
            {result.characters.map((char, i) => (
              <CharacterSelectCard
                key={i}
                character={char}
                isSelected={currentPlayerName === char.name}
                autoDetected={char.isPlayerCharacter && !state.selectedPlayerCharacter}
                onSelect={() => update({ selectedPlayerCharacter: char.name })}
                onEdit={() => setEditingCharacter(char)}
                onDelete={() => {
                  const updatedCharacters = result.characters.filter(c => c.name !== char.name);
                  updateResult({ characters: updatedCharacters });
                  // Clear player selection if deleting the selected character
                  if (currentPlayerName === char.name) {
                    update({ selectedPlayerCharacter: null });
                  }
                }}
              />
            ))}

            {/* Observer row — same radio affordance as the character cards */}
            <button
              onClick={() => update({ selectedPlayerCharacter: '_none_' })}
              role="radio"
              aria-checked={state.selectedPlayerCharacter === '_none_'}
              aria-label="Observer — create your character during play"
              className={`w-full p-3 rounded text-left transition-colors flex items-center ${
                state.selectedPlayerCharacter === '_none_'
                  ? 'bg-dark-500 text-dark-50 ring-2 ring-dark-400'
                  : 'bg-dark-600/50 text-dark-400 hover:bg-dark-500/50'
              }`}
            >
              <span
                className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center transition-colors mr-3 ${
                  state.selectedPlayerCharacter === '_none_'
                    ? 'border-dark-200 bg-dark-400'
                    : 'border-dark-300 hover:border-primary-400'
                }`}
                aria-hidden="true"
              >
                {state.selectedPlayerCharacter === '_none_' && <span className="w-2 h-2 rounded-full bg-dark-50" />}
              </span>
              <span className="mr-2" aria-hidden="true">👁️</span>
              <span className="text-sm">Observer / Create character during play</span>
            </button>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="text-amber-400 text-3xl mb-2">🔍</div>
            <p className="text-dark-300 text-sm mb-3">
              No characters extracted. Click "Find Missing" to search again.
            </p>
          </div>
        )}
      </div>
      
      {/* Initial Situation - EDITABLE */}
      <div className="bg-dark-700 rounded p-4">
        <h4 className="text-sm font-medium text-dark-300 mb-2">
          Opening Hook
          <span className="text-dark-400 font-normal ml-2">(editable)</span>
        </h4>
        <textarea
          value={result.initialSituation || ''}
          onChange={(e) => updateResult({ initialSituation: e.target.value })}
          className="narrative-input w-full min-h-[120px] px-4 py-3 text-sm"
          placeholder="Describe the opening scene or situation that starts the story..."
        />
        <p className="text-xs text-dark-400 mt-2">
          This text will be used to generate the opening narrative when you enter the world.
        </p>
      </div>
      
      {/* Edit Character Modal */}
      {editingCharacter && (() => {
        // Find the user's original character stub (raw input from Step 3)
        const originalStub = state.guidedDraft?.characterStubs?.find(
          s => s.name === editingCharacter.name
        );
        
        return (
          <EditCharacterModal
            character={editingCharacter}
            isOpen={!!editingCharacter}
            onClose={() => setEditingCharacter(null)}
            onSave={handleCharacterSave}
            worldPrompt={state.prompt || `[Guided Creation] ${result.settingDefinition?.substring(0, 200)}`}
            settingDefinition={result.settingDefinition}
            guidedContext={state.mode === 'guided' && state.guidedDraft ? {
              // User's RAW input - most important!
              originalCharacterConcept: originalStub?.oneLinerConcept,
              originalRole: originalStub?.roleArchetype,
              // World foundation
              genre: state.guidedDraft.foundation?.genre,
              tone: state.guidedDraft.foundation?.tone,
              themes: state.guidedDraft.foundation?.themes,
              worldRules: state.guidedDraft.setting?.worldRules,
              keyLocations: state.guidedDraft.setting?.keyLocations?.map(l => ({
                name: l.name,
                description: l.description || '',
              })),
              // Conflict from user input
              conflict: state.guidedDraft.conflict ? {
                centralConflict: state.guidedDraft.conflict.centralConflict,
                stakes: state.guidedDraft.conflict.stakes,
              } : undefined,
              // Opening from user input
              openingScene: state.guidedDraft.opening?.initialSituation,
              startingLocation: state.guidedDraft.opening?.startingLocation,
              otherCharacters: result.characters
                .filter(c => c.name !== editingCharacter.name)
                .map(c => ({
                  name: c.name,
                  roleInStory: c.roleInStory ?? undefined,
                  primaryDrive: c.primaryDrive ?? undefined,
                })),
              relationships: state.guidedDraft.relationships?.map(r => ({
                fromCharacter: r.fromCharacter,
                toCharacter: r.toCharacter,
                surfaceRelationship: r.surfaceRelationship,
              })),
            } : undefined}
          />
        );
      })()}
    </div>
  );
}

// Unified character card with selection + expandable details
function CharacterSelectCard({
  character,
  isSelected,
  autoDetected,
  onSelect,
  onEdit,
  onDelete
}: {
  character: ScaffoldedCharacter;
  isSelected: boolean;
  autoDetected?: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded overflow-hidden transition-colors ${
      isSelected
        ? 'bg-primary-600 ring-2 ring-primary-400'
        : 'bg-dark-600 hover:bg-dark-500'
    }`}>
      {/* Main row: radio select + edit (body) + delete + expand */}
      <div className="flex items-center">
        {/* Radio: play as this character */}
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          role="radio"
          aria-checked={isSelected}
          aria-label={`Play as ${character.name}`}
          title="Play as this character"
          className="pl-3 pr-1 py-3 flex items-center justify-center"
        >
          <span
            className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center transition-colors ${
              isSelected
                ? 'border-primary-200 bg-primary-500'
                : 'border-dark-300 hover:border-primary-400'
            }`}
          >
            {isSelected && <span className="w-2 h-2 rounded-full bg-dark-50" />}
          </span>
        </button>

        {/* Card body opens the edit modal */}
        <button
          onClick={onEdit}
          title="Click to edit this character"
          className="flex-1 py-3 pr-3 pl-2 text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <span className={`font-medium ${isSelected ? 'text-dark-50' : 'text-dark-100'}`}>
                {character.name}
              </span>
              {character.roleInStory && (
                <span className={`text-sm ml-2 ${isSelected ? 'text-primary-200' : 'text-dark-400'}`}>
                  — {character.roleInStory}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isSelected && (
                <span className="text-xs bg-primary-500/50 px-2 py-0.5 rounded text-white">Player</span>
              )}
              {autoDetected && (
                <span className="text-xs bg-green-500/50 px-2 py-0.5 rounded text-white">Auto-detected</span>
              )}
            </div>
          </div>
        </button>

        {/* Delete button */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className={`p-3 transition-colors ${
            isSelected
              ? 'text-primary-200 hover:text-red-300'
              : 'text-dark-400 hover:text-red-400'
          }`}
          title="Delete character"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>

        {/* Expand button */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`p-3 transition-colors ${
            isSelected
              ? 'text-primary-200 hover:text-dark-50'
              : 'text-dark-400 hover:text-dark-50'
          }`}
          title={expanded ? 'Collapse details' : 'Expand details'}
          aria-expanded={expanded}
        >
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      {/* Expanded details */}
      {expanded && (
        <div className={`px-3 pb-3 space-y-1 text-sm ${isSelected ? 'text-primary-100' : ''}`}>
          {character.physicalDescription && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Appearance:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.physicalDescription}</span>
            </div>
          )}
          {character.age && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Age:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.age}</span>
            </div>
          )}
          {character.primaryDrive && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Primary Drive:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.primaryDrive}</span>
            </div>
          )}
          {character.defenseMechanisms?.length > 0 && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Defense Mechanisms:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.defenseMechanisms.join(', ')}</span>
            </div>
          )}
          {character.surfaceGoals?.length > 0 && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Goals:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>
                {character.surfaceGoals.join(', ')}
              </span>
            </div>
          )}
          {character.secrets?.length > 0 && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Secrets:</span>
              <span className={`ml-2 italic ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.secrets.join(', ')}</span>
            </div>
          )}
          {character.speechPatterns && (
            <div>
              <span className={isSelected ? 'text-primary-200' : 'text-dark-400'}>Speech:</span>
              <span className={`ml-2 ${isSelected ? 'text-dark-50' : 'text-dark-200'}`}>{character.speechPatterns}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Keep CharacterCard for backward compatibility but mark it unused
function CharacterCard({ character, isSelected, onEdit }: { character: ScaffoldedCharacter; isSelected?: boolean; onEdit?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`bg-dark-700 rounded overflow-hidden ${isSelected ? 'ring-2 ring-primary-500' : ''}`}>
      <div className="w-full p-3 flex items-center justify-between">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 flex items-center text-left"
        >
          <div className="flex-1">
            <span className="text-dark-50 font-medium">{character.name}</span>
            {isSelected && (
              <span className="ml-2 px-2 py-0.5 bg-primary-600 text-white text-xs rounded">Your Character</span>
            )}
            {character.roleInStory && (
              <span className="ml-2 text-dark-400 text-sm">— {character.roleInStory}</span>
            )}
          </div>
          <svg
            className={`w-4 h-4 text-dark-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="ml-2 p-1.5 text-dark-400 hover:text-primary-400 hover:bg-dark-600 rounded transition-colors"
            title="Edit character"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 space-y-2 text-sm">
          {character.physicalDescription && (
            <div>
              <span className="text-dark-400">Appearance:</span>
              <span className="text-dark-200 ml-2">{character.physicalDescription}</span>
            </div>
          )}
          {character.age && (
            <div>
              <span className="text-dark-400">Age:</span>
              <span className="text-dark-200 ml-2">{character.age}</span>
            </div>
          )}
          {character.intelligenceSignature && (
            <div>
              <span className="text-dark-400">Intelligence:</span>
              <span className="text-dark-200 ml-2">{character.intelligenceSignature}</span>
            </div>
          )}
          {character.knowledgeDomains && character.knowledgeDomains.length > 0 && (
            <div>
              <span className="text-dark-400">Knowledge:</span>
              <span className="text-dark-200 ml-2">{character.knowledgeDomains.join(', ')}</span>
            </div>
          )}
          {character.surfaceGoals && character.surfaceGoals.length > 0 && (
            <div>
              <span className="text-dark-400">Goals:</span>
              <span className="text-dark-200 ml-2">{character.surfaceGoals.join(', ')}</span>
            </div>
          )}
          {character.secrets.length > 0 && (
            <div>
              <span className="text-dark-400">Secrets:</span>
              <span className="text-dark-200 ml-2 italic">{character.secrets.join(', ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
